<template>
    <v-container fluid no-gutters class="mx-0 px-0" :class="{ 'stats-root--landscape': orientation === 'landscape' }">
        <!-- Attach overlay to body and bump z-index so it sits above v-dialogs (v-dialog uses portals) -->
        <v-overlay :value="overlay" attach="body" absolute :style="{ zIndex: 2147483647 }">
            <v-progress-circular
                color="primary"
                indeterminate
                size="64"
                :style="{ zIndex: 2147483647 }"
            />
        </v-overlay>
        <!-- while a stat is saving: block taps without dimming the screen -->
        <div v-if="saving" class="saving-blocker">
            <div class="saving-pill">
                <v-progress-circular indeterminate size="14" width="2" color="white" class="mr-2" />Saving…
            </div>
        </div>
        <v-snackbar
            v-model="statToast"
            class="stat-toast"
            :color="statToastNeutral ? 'grey darken-3' : 'green darken-1'"
            :timeout="statToastNeutral ? 1600 : 3000"
            top
        >
            <span class="stat-toast__text">
                <v-icon small color="white" class="mr-1">{{ statToastNeutral ? 'mdi-information-outline' : 'mdi-check-circle' }}</v-icon>
                {{ statToastText }}
            </span>
            <template v-slot:action="{ attrs }">
                <v-btn
                    v-if="!statToastNeutral"
                    text
                    color="white"
                    class="stat-toast__undo"
                    v-bind="attrs"
                    :loading="undoing"
                    :disabled="!statToastLogIds.length"
                    @click="undoLastStat()"
                >
                    <v-icon left small>mdi-undo</v-icon>Undo
                </v-btn>
            </template>
        </v-snackbar>
        <div class="p-scoreboard" :class="{ 'p-scoreboard--wide': orientation === 'landscape' }">
            <div class="p-scoreboard__meta">
                <span class="p-scoreboard__meta-spacer" />
                <div class="p-scoreboard__meta-text">
                    <div class="p-scoreboard__title">
                        {{ getMatchTitle() }} · {{ phaseLabel }} · Round {{ match.round }}
                    </div>
                    <div class="p-scoreboard__date">{{ matchDate }}</div>
                </div>
                <div class="p-scoreboard__meta-side">
                    <!-- one place for everything about how the page looks: layout + the alternative entry page -->
                    <v-menu offset-y left :z-index="20" min-width="250">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn small text class="p-view-toggle" title="Change view" v-bind="attrs" v-on="on">
                                <v-icon small :left="$vuetify.breakpoint.smAndUp">mdi-view-dashboard-outline</v-icon>
                                <span class="d-none d-sm-inline">View</span>
                                <v-icon small>mdi-menu-down</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense class="view-menu">
                            <v-subheader>Layout</v-subheader>
                            <v-list-item @click="setOrientationOverride('portrait')">
                                <v-list-item-icon class="mr-3">
                                    <v-icon>mdi-cellphone</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Phone layout</v-list-item-title>
                                    <v-list-item-subtitle>One column, for holding upright</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="orientation === 'portrait'">
                                    <v-icon small color="primary">mdi-check</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-list-item @click="setOrientationOverride('landscape')">
                                <v-list-item-icon class="mr-3">
                                    <v-icon>mdi-tablet</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Tablet layout</v-list-item-title>
                                    <v-list-item-subtitle>Both teams side by side</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="orientation === 'landscape'">
                                    <v-icon small color="primary">mdi-check</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider class="my-1" />
                            <v-subheader>Entry</v-subheader>
                            <v-list-item @click="setEntryMode('pick')">
                                <v-list-item-icon class="mr-3">
                                    <v-icon>mdi-gesture-tap-button</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Stat first</v-list-item-title>
                                    <v-list-item-subtitle>Tap the stat, then pick the player</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="!isSelectMode">
                                    <v-icon small color="primary">mdi-check</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-list-item @click="setEntryMode('select')">
                                <v-list-item-icon class="mr-3">
                                    <v-icon>mdi-account-check-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Player first</v-list-item-title>
                                    <v-list-item-subtitle>Select a player or team, then the stat</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="isSelectMode">
                                    <v-icon small color="primary">mdi-check</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider class="my-1" />
                            <v-subheader>Advanced stats</v-subheader>
                            <v-list-item @click="setAdvancedStats(!advancedStats)">
                                <v-list-item-icon class="mr-3">
                                    <v-icon>mdi-tag-multiple-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Shot details</v-list-item-title>
                                    <v-list-item-subtitle>Situation, play, role and shot type after each 2PT / 3PT</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-icon small :color="advancedStats ? 'primary' : 'grey'">{{ advancedStats ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider class="my-1" />
                            <v-subheader>Teams</v-subheader>
                            <v-list-item @click="openColorDialog()">
                                <v-list-item-icon class="mr-3">
                                    <v-icon>mdi-palette-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Match colours</v-list-item-title>
                                    <v-list-item-subtitle>The colour of each team's buttons</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
            <div class="p-scoreboard__teams">
                <div class="p-team" :style="teamStyle(matchColorOf(match.team1))">
                    <v-img class="p-team__logo" width="30" height="30" contain :src="googleApiHost + '/' + match.team1.logo" />
                    <div class="p-team__name">{{ match.team1.name }}</div>
                    <button type="button" class="p-team__roster" @click.stop="setGameRoster(1)">
                        <v-icon x-small :color="textOn(matchColorOf(match.team1))">mdi-clipboard-account-outline</v-icon> Roster
                    </button>
                </div>
                <div class="p-score">
                    <div class="p-score__value">
                        <span>{{ match.scoreTeam1 }}</span>
                        <span class="p-score__dash">–</span>
                        <span>{{ match.scoreTeam2 }}</span>
                    </div>
                    <div class="p-score__fouls">
                        Fouls {{ team1Fouls }} · {{ team2Fouls }}
                    </div>
                </div>
                <div class="p-team" :style="teamStyle(matchColorOf(match.team2))">
                    <v-img class="p-team__logo" width="30" height="30" contain :src="googleApiHost + '/' + match.team2.logo" />
                    <div class="p-team__name">{{ match.team2.name }}</div>
                    <button type="button" class="p-team__roster" @click.stop="setGameRoster(2)">
                        <v-icon x-small :color="textOn(matchColorOf(match.team2))">mdi-clipboard-account-outline</v-icon> Roster
                    </button>
                </div>
            </div>
            <stats-clock
                v-if="orientation === 'portrait'"
                :quarter.sync="quarter"
                :quarter-items="quarterItems"
                :quarter-label="quarterLabel"
                :running="running"
                :time="`${minutes}:${seconds}`"
                :match-id="match.id"
                :sync="clockSync"
                @toggle="switchTimer()"
                @edit="timerDialog = true"
                @timeout="timeout()"
                @pause="pauseTimer()"
                @resync="resyncClock()"
            />
        </div>
        <template v-if="isSelectMode">
            <stats-select-portrait
                v-if="orientation === 'portrait'"
                :teams="teamViews"
                :selection="selection"
                :actions="selectActions"
                :action-color="selectActionColor"
                :action-light="isLightColor(selectActionColor)"
                :open-bench-side="openBenchSide"
                @select-team="selectTeam"
                @player-click="onSelectPlayer"
                @action="runSelectAction"
                @toggle-bench="toggleBench"
                @end-match="endMatchDialog = true"
            >
                <template v-slot:feed>
                    <stats-feed v-bind="feedProps" v-on="feedListeners" />
                </template>
                <template v-slot:selection>
                    <div class="s-selection" :class="{ 's-selection--empty': !currentTeam }">
                        <template v-if="selectedPlayer">
                            <span class="s-selection__jersey" :style="teamStyle(currentColor)">{{ jerseyOf(selectedPlayer) }}</span>
                            <span class="s-selection__who">
                                {{ selectedPlayer.name }} {{ selectedPlayer.surname }}
                                <small>{{ currentTeamName }}</small>
                            </span>
                            <stats-jersey-edit
                                class="s-selection__jersey-edit"
                                :value="selectedPlayer.alternativeJersey"
                                :official="selectedPlayer.jersey"
                                light
                                @save="updateSelectedPlayerAlternativeJersey"
                            />
                        </template>
                        <template v-else-if="currentTeam">
                            <span class="s-selection__jersey" :style="teamStyle(currentColor)"><v-icon small :color="textOn(currentColor)">mdi-account-group</v-icon></span>
                            <span class="s-selection__who">Team<small>{{ currentTeamName }}</small></span>
                        </template>
                        <span v-else class="s-selection__hint">
                            <v-icon small color="primary">mdi-gesture-tap</v-icon>Tap a player or a team, then the stat
                        </span>
                        <v-btn v-if="currentTeam" icon small title="Clear selection" class="ml-auto" @click="clearSelection()">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </template>
            </stats-select-portrait>
            <stats-select-landscape
                v-else
                :teams="teamViews"
                :selection="selection"
                :actions="selectActions"
                :action-color="selectActionColor"
                :action-light="isLightColor(selectActionColor)"
                :open-bench-side="openBenchSide"
                @select-team="selectTeam"
                @player-click="onSelectPlayer"
                @action="runSelectAction"
                @toggle-bench="toggleBench"
                @open-team-stats="teamStatsDialog = true"
                @open-feed="feedDialog = true"
                @end-match="endMatchDialog = true"
            >
                <template v-slot:clock>
                    <stats-clock
                        :quarter.sync="quarter"
                        :quarter-items="quarterItems"
                        :quarter-label="quarterLabel"
                        :running="running"
                        :time="`${minutes}:${seconds}`"
                        :match-id="match.id"
                        :sync="clockSync"
                        @toggle="switchTimer()"
                        @edit="timerDialog = true"
                        @timeout="timeout()"
                        @pause="pauseTimer()"
                        @resync="resyncClock()"
                    />
                </template>
                <template v-slot:selection>
                    <div class="s-selection" :class="{ 's-selection--empty': !currentTeam }">
                        <template v-if="selectedPlayer">
                            <span class="s-selection__jersey" :style="teamStyle(currentColor)">{{ jerseyOf(selectedPlayer) }}</span>
                            <span class="s-selection__who">
                                {{ selectedPlayer.name }} {{ selectedPlayer.surname }}
                                <small>{{ currentTeamName }}</small>
                            </span>
                            <stats-jersey-edit
                                class="s-selection__jersey-edit"
                                :value="selectedPlayer.alternativeJersey"
                                :official="selectedPlayer.jersey"
                                light
                                @save="updateSelectedPlayerAlternativeJersey"
                            />
                        </template>
                        <template v-else-if="currentTeam">
                            <span class="s-selection__jersey" :style="teamStyle(currentColor)"><v-icon small :color="textOn(currentColor)">mdi-account-group</v-icon></span>
                            <span class="s-selection__who">Team<small>{{ currentTeamName }}</small></span>
                        </template>
                        <span v-else class="s-selection__hint">
                            <v-icon small color="primary">mdi-gesture-tap</v-icon>Tap a player or a team, then the stat
                        </span>
                        <v-btn v-if="currentTeam" icon small title="Clear selection" class="ml-auto" @click="clearSelection()">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </template>
            </stats-select-landscape>
        </template>
        <stats-portrait
            v-else-if="orientation === 'portrait'"
            :teams="teamViews"
            :open-bench-side="openBenchSide"
            @quick-action="runQuickAction"
            @player-click="onPlayerClick"
            @toggle-bench="toggleBench"
            @end-match="endMatchDialog = true"
        >
            <template v-slot:feed>
                <stats-feed v-bind="feedProps" v-on="feedListeners" />
            </template>
        </stats-portrait>
        <stats-landscape
            v-else
            :teams="teamViews"
            :open-bench-side="openBenchSide"
            :team-stat-rows="teamStatRows"
            @shot="onLandscapeShot"
            @action="runLandscapeAction"
            @player-click="onPlayerClick"
            @toggle-bench="toggleBench"
            @end-match="endMatchDialog = true"
        >
            <template v-slot:clock>
                <stats-clock
                    stacked
                    :quarter.sync="quarter"
                    :quarter-items="quarterItems"
                    :quarter-label="quarterLabel"
                    :running="running"
                    :time="`${minutes}:${seconds}`"
                    :match-id="match.id"
                    :sync="clockSync"
                    @toggle="switchTimer()"
                    @edit="timerDialog = true"
                    @timeout="timeout()"
                    @pause="pauseTimer()"
                    @resync="resyncClock()"
                />
            </template>
            <template v-slot:feed>
                <stats-feed fill v-bind="feedProps" v-on="feedListeners" />
            </template>
        </stats-landscape>
        <!-- ===== shared dialogs (portrait + landscape) ===== -->
        <!-- the match colour of each team: its buttons here, saved on the team so its next matches have it too -->
        <v-dialog v-model="colorDialog" max-width="560px" scrollable>
            <v-card>
                <v-card-title class="text-h6">Match colours</v-card-title>
                <v-card-subtitle class="pt-1">The colour of each team's buttons. Without one, the team colour is used.</v-card-subtitle>
                <v-card-text>
                    <div v-for="draft in colorDrafts" :key="draft.side" class="match-colour">
                        <div class="match-colour__head">
                            <v-img class="match-colour__logo" max-width="28" height="28" contain :src="googleApiHost + '/' + draft.team.logo" />
                            <span class="match-colour__name">{{ draft.team.name }}</span>
                            <span class="match-colour__sample" :style="teamStyle(draft.color || draft.team.color)">2PT</span>
                        </div>
                        <div class="match-colour__swatches">
                            <button
                                v-for="swatch in colorSwatches(draft.team)"
                                :key="swatch"
                                type="button"
                                class="match-colour__swatch"
                                :class="{ 'match-colour__swatch--on': String(draft.color || draft.team.color).toLowerCase() === swatch }"
                                :style="{ background: swatch }"
                                :title="swatch"
                                @click="draft.color = swatch"
                            />
                        </div>
                        <div class="match-colour__custom">
                            <label class="match-colour__picker">
                                <input type="color" :value="draft.color || draft.team.color" @input="draft.color = $event.target.value.toLowerCase()">
                                <v-icon small>mdi-eyedropper-variant</v-icon> Other colour
                            </label>
                            <v-btn text small :disabled="!draft.color" @click="draft.color = null">
                                <v-icon left small>mdi-restore</v-icon>Team colour
                            </v-btn>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="colorSaving" @click="colorDialog = false">Cancel</v-btn>
                    <v-btn depressed color="primary" class="white--text" :loading="colorSaving" @click="saveMatchColors()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="unavailableDialog"
            max-width="400px"
        >
            <v-card v-if="unavailablePlayer" class="sd-card">
                <div class="sd-unavailable__head">
                    <div class="sd-unavailable__photo">
                        <v-icon large color="grey"> mdi-account </v-icon>
                    </div>
                    <div>
                        <div class="sd-unavailable__label">
                            <v-icon small color="red"> mdi-alert-circle-outline </v-icon> Player unavailable
                        </div>
                        <div class="sd-unavailable__name">#{{ jerseyOf(unavailablePlayer) }} {{ unavailablePlayer.name }} {{ unavailablePlayer.surname }}</div>
                    </div>
                </div>
                <v-card-text class="pt-0">
                    <div class="sd-section-label">Reason</div>
                    <div v-for="reason in unavailableReasons(unavailablePlayer)" :key="reason" class="sd-reason">
                        <v-icon small color="red">mdi-close-circle-outline</v-icon>{{ reason }}
                    </div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text large class="flex-grow-1 sd-btn" @click="unavailableDialog = false; unavailablePlayer = null;">
                        Close
                    </v-btn>
                    <v-btn v-if="unavailablePlayer.doctorCheck !== true" depressed large color="green" class="flex-grow-1 white--text sd-btn" :loading="doctorCheckLoading" @click="setDoctorCheck(unavailablePlayer)">
                        <v-icon left> mdi-stethoscope </v-icon>
                        Doctor check OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="gameRosterDialog"
            width="100%"
            hide-overlay
            persistent
            transition="dialog-bottom-transition"
            :fullscreen="true"
        >
            <v-card class="sd-fullscreen">
                <stats-dialog-bar
                    title="Game roster"
                    :subtitle="currentTeamName"
                    :count="(currentTeam == 1 ? match.gameRosterTeam1 : match.gameRosterTeam2).length"
                    :color="currentColor"
                    :text-color="textOn(currentColor)"
                    @close="gameRosterDialog = false; currentTeam = null;"
                >
                    <template v-slot:actions>
                        <!-- the QR of the athlete's health card puts the player in the roster -->
                        <v-btn depressed small color="white" class="sd-btn mr-2" @click="openRosterScanner()">
                            <v-icon left small>mdi-qrcode-scan</v-icon>Scan QR
                        </v-btn>
                        <v-btn depressed small color="white" class="sd-btn" @click="saveGameRoster()">
                            <v-icon left small>mdi-content-save-outline</v-icon>Save
                        </v-btn>
                    </template>
                </stats-dialog-bar>
                <qr-scanner
                    v-model="rosterScanner"
                    :title="`Scan cards · ${currentTeamName}`"
                    hint="Hold the QR of the athlete's health card to the camera: the player goes in the roster. Press Save when done."
                    :status="rosterScanStatus"
                    @scan="onRosterScan"
                />
                <v-row class="mx-0 px-2 py-3">
                    <v-col :cols="orientation === 'portrait' ? 12 : 6" class="px-2">
                        <div class="sd-section-label">In the roster · {{ currentTeamPlayers.filter(player => inRoster(player)).length }}</div>
                        <stats-player-row
                            v-for="player in currentTeamPlayers.filter(player => inRoster(player))"
                            :key="`roster-in-${player.id}`"
                            class="mb-2"
                            :player="player"
                            :jersey="jerseyOf(player)"
                            :color="currentColor"
                            :text-color="textOn(currentColor)"
                            :reasons="unavailableReasons(player)"
                            :clickable="!isAvailable(player)"
                            @click="isAvailable(player) || openUnavailableDialog(player)"
                        >
                            <v-btn small depressed color="red darken-1" class="white--text sd-btn" @click="removeFromRoster(player)">
                                <v-icon left small>mdi-arrow-left</v-icon>Out
                            </v-btn>
                        </stats-player-row>
                        <div v-if="!currentTeamPlayers.some(player => inRoster(player))" class="sd-empty">Nobody in the roster yet</div>
                    </v-col>
                    <v-col :cols="orientation === 'portrait' ? 12 : 6" class="px-2">
                        <div class="sd-section-label">Not in the roster · {{ currentTeamPlayers.filter(player => !inRoster(player)).length }}</div>
                        <stats-player-row
                            v-for="player in currentTeamPlayers.filter(player => !inRoster(player))"
                            :key="`roster-out-${player.id}`"
                            class="mb-2"
                            :player="player"
                            :jersey="jerseyOf(player)"
                            :color="currentColor"
                            :text-color="textOn(currentColor)"
                            :reasons="unavailableReasons(player)"
                            :clickable="!isAvailable(player)"
                            @click="isAvailable(player) || openUnavailableDialog(player)"
                        >
                            <v-btn v-if="isAvailable(player)" small depressed color="green" class="white--text sd-btn" @click="addToRoster(player)">
                                In<v-icon right small>mdi-arrow-right</v-icon>
                            </v-btn>
                            <v-btn v-else small outlined color="red" class="sd-btn" @click="openUnavailableDialog(player)">
                                <v-icon small left> {{ player.doctorCheck !== true ? 'mdi-stethoscope' : 'mdi-information-outline' }} </v-icon>
                                {{ player.doctorCheck !== true ? 'Doctor check' : 'Details' }}
                            </v-btn>
                        </stats-player-row>
                        <div v-if="!currentTeamPlayers.some(player => !inRoster(player))" class="sd-empty">Everyone is in the roster</div>
                    </v-col>
                </v-row>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="playersDialog"
            width="100%"
            hide-overlay
            persistent
            transition="dialog-bottom-transition"
            :fullscreen="true"
        >
            <v-card class="sd-fullscreen">
                <stats-dialog-bar
                    title="Substitutions"
                    :subtitle="currentTeamName"
                    :color="currentColor"
                    :text-color="textOn(currentColor)"
                    @close="playersDialog = false; swapOutPlayer = null; isSelectMode && clearSelection()"
                >
                    <template v-slot:actions>
                        <v-btn depressed small color="white" class="sd-btn" @click="subOutAll()">
                            <v-icon left small>mdi-exit-run</v-icon>All out
                        </v-btn>
                    </template>
                </stats-dialog-bar>
                <v-row class="mx-0 px-2 py-3">
                    <v-col :cols="orientation === 'portrait' ? 12 : 6" class="px-2">
                        <div class="sd-section-label">
                            On court · {{ currentTeam == 1 ? team1SelectedPlayers.length : team2SelectedPlayers.length }} / 5
                        </div>
                        <div class="sd-swap-hint">
                            <v-icon small color="primary">mdi-gesture-tap</v-icon>
                            {{ swapOutPlayer ? `${playerLabel(swapOutPlayer)} comes off: tap who goes in` : 'Tap a player to swap him with someone from the bench' }}
                        </div>
                        <stats-player-row
                            v-for="player in getPlayers().filter(player => isOnCourt(player))"
                            :key="`court-${player.id}`"
                            class="mb-2"
                            :class="{ 'player-card--swap': swapOutPlayer && swapOutPlayer.id === player.id }"
                            :player="player"
                            :jersey="jerseyOf(player)"
                            :color="currentColor"
                            :text-color="textOn(currentColor)"
                            :reasons="unavailableReasons(player)"
                            clickable
                            @click="toggleSwapOut(player)"
                        >
                            <v-btn small depressed color="red darken-1" class="white--text sd-btn" @click="removePlayer(player)">
                                <v-icon left small>mdi-arrow-left</v-icon>Out
                            </v-btn>
                        </stats-player-row>
                        <div v-if="!getPlayers().some(player => isOnCourt(player))" class="sd-empty">No players on court</div>
                    </v-col>
                    <v-col :cols="orientation === 'portrait' ? 12 : 6" class="px-2">
                        <div class="sd-section-label">Bench · {{ getPlayers().filter(player => !isOnCourt(player)).length }}</div>
                        <stats-player-row
                            v-for="player in getPlayers().filter(player => !isOnCourt(player))"
                            :key="`bench-${player.id}`"
                            class="mb-2"
                            :player="player"
                            :jersey="jerseyOf(player)"
                            :color="currentColor"
                            :text-color="textOn(currentColor)"
                            :reasons="unavailableReasons(player)"
                            :clickable="!isAvailable(player)"
                            @click="isAvailable(player) || openUnavailableDialog(player)"
                        >
                            <v-btn
                                v-if="isAvailable(player)"
                                small
                                depressed
                                color="green"
                                class="white--text sd-btn"
                                :disabled="!swapOutPlayer && (currentTeam === 1 ? team1SelectedPlayers : team2SelectedPlayers).length >= 5"
                                @click="subIn(player)"
                            >
                                {{ swapOutPlayer ? 'Swap in' : 'In' }}<v-icon right small>{{ swapOutPlayer ? 'mdi-swap-horizontal' : 'mdi-arrow-right' }}</v-icon>
                            </v-btn>
                            <v-btn v-else small outlined color="red" class="sd-btn" @click="openUnavailableDialog(player)">
                                <v-icon small left> {{ player.doctorCheck !== true ? 'mdi-stethoscope' : 'mdi-information-outline' }} </v-icon>
                                {{ player.doctorCheck !== true ? 'Doctor check' : 'Details' }}
                            </v-btn>
                        </stats-player-row>
                        <div v-if="!getPlayers().some(player => !isOnCourt(player))" class="sd-empty">Nobody on the bench</div>
                    </v-col>
                </v-row>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="dialog"
            width="100%"
            hide-overlay
            persistent
            transition="dialog-bottom-transition"
            :fullscreen="true"
        >
            <v-card class="sd-fullscreen">
                <stats-dialog-bar
                    v-if="statDialog"
                    :title="`${selectedPlayer.name} ${selectedPlayer.surname || ''}`.trim()"
                    :subtitle="currentTeamName"
                    :color="currentColor"
                    :text-color="textOn(currentColor)"
                    @close="closeStatDialog()"
                >
                    <div class="sd-jersey-edit">
                        <stats-jersey-edit
                            :value="selectedPlayer.alternativeJersey"
                            :official="selectedPlayer.jersey"
                            :text-color="textOn(currentColor)"
                            :light="isLightColor(currentColor)"
                            @save="updateSelectedPlayerAlternativeJersey"
                        />
                    </div>
                </stats-dialog-bar>
                <stats-dialog-bar
                    v-else
                    :title="reboundDialog ? 'Rebound' : getCategoryText(category)"
                    :subtitle="currentTeamName"
                    :color="currentColor"
                    :text-color="textOn(currentColor)"
                    @close="closeStatDialog()"
                />

                <div v-if="statDialog" class="sd-stat">
                    <div class="sd-section-label">Made</div>
                    <div class="sd-grid sd-grid--3">
                        <v-btn class="sd-stat-btn" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=true; pointsDialog = true; category = '1pt'; statDialog = false; setChoice('made');">
                            <span class="sd-stat-btn__big">1</span>PT
                        </v-btn>
                        <v-btn class="sd-stat-btn" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=true; pointsDialog = true; category = '2pts'; statDialog = false; setChoice('made');">
                            <span class="sd-stat-btn__big">2</span>PT
                        </v-btn>
                        <v-btn class="sd-stat-btn" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=true; pointsDialog = true; category = '3pts'; statDialog = false; setChoice('made');">
                            <span class="sd-stat-btn__big">3</span>PT
                        </v-btn>
                    </div>
                    <div class="sd-section-label">Missed</div>
                    <div class="sd-grid sd-grid--3">
                        <v-btn class="sd-stat-btn sd-stat-btn--missed" depressed :style="{ borderColor: currentColor }" @click="dialog=true; pointsDialog = true; statDialog = false; category = '1pt'; setChoice('missed');">
                            <v-icon small color="red">mdi-close</v-icon><span class="sd-stat-btn__big">1</span>PT
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--missed" depressed :style="{ borderColor: currentColor }" @click="dialog=true; pointsDialog = true; statDialog = false; category = '2pts'; setChoice('missed');">
                            <v-icon small color="red">mdi-close</v-icon><span class="sd-stat-btn__big">2</span>PT
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--missed" depressed :style="{ borderColor: currentColor }" @click="dialog=true; pointsDialog = true; statDialog = false; category = '3pts'; setChoice('missed');">
                            <v-icon small color="red">mdi-close</v-icon><span class="sd-stat-btn__big">3</span>PT
                        </v-btn>
                    </div>
                    <div class="sd-section-label">Other</div>
                    <div class="sd-grid sd-grid--2">
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="playersDialog=true; statDialog = false; dialog=false; removePlayer(selectedPlayer);">
                            <v-icon left>mdi-swap-horizontal</v-icon>Sub
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=true; foulsDialog = true; category = 'foul'; statDialog = false;">
                            <v-icon left>mdi-whistle-outline</v-icon>Foul
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=false; category = 'dreb'; statDialog = false; setStat(selectedPlayer);">
                            <v-icon left>mdi-shield-outline</v-icon>Def. reb
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=false; category = 'oreb'; statDialog = false; setStat(selectedPlayer);">
                            <v-icon left>mdi-arrow-up-bold-outline</v-icon>Off. reb
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=false; category = 'asst'; statDialog = false; setStat(selectedPlayer);">
                            <v-icon left>mdi-handshake-outline</v-icon>Assist
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=false; category = 'stl'; statDialog = false; setStat(selectedPlayer);">
                            <v-icon left>mdi-hand-back-right-outline</v-icon>Steal
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=false; category = 'blk'; statDialog = false; setStat(selectedPlayer);">
                            <v-icon left>mdi-cancel</v-icon>Block
                        </v-btn>
                        <v-btn class="sd-stat-btn sd-stat-btn--other" depressed :dark="!isLightColor(currentColor)" :light="isLightColor(currentColor)" :color="currentColor" @click="dialog=false; category = 'to'; statDialog = false; setStat(selectedPlayer);">
                            <v-icon left>mdi-alert-circle-outline</v-icon>Turnover
                        </v-btn>
                    </div>
                </div>

                <div v-else-if="reboundDialog" class="sd-rebound">
                    <div v-if="pendingMiss" class="sd-rebound__miss">
                        <v-icon small color="red">mdi-close-circle</v-icon>
                        Missed {{ pendingMiss.category === '3pts' ? '3PT' : '2PT' }} by {{ playerLabel(pendingMiss.player) }}: pick the rebound
                    </div>
                    <div class="sd-rebound__cols">
                        <div v-for="side in reboundSides" :key="`reb-${side}`" class="sd-rebound__col">
                            <div class="sd-rebound__title" :style="teamStyle(side === 'dreb' ? opponentColor : currentColor)">
                                <v-icon small :color="textOn(side === 'dreb' ? opponentColor : currentColor)">{{ side === 'dreb' ? 'mdi-shield-outline' : 'mdi-arrow-up-bold-outline' }}</v-icon>
                                {{ side === 'dreb' ? 'Defensive' : 'Offensive' }}
                                <small>{{ side === 'dreb' ? (currentTeam === 1 ? match.team2.name : match.team1.name) : currentTeamName }}</small>
                            </div>
                            <!-- shooter got his own rebound and scored: recorded as one put back -->
                            <button
                                v-if="side === 'oreb' && pendingMiss && pendingMiss.player"
                                type="button"
                                class="sd-rebound__player sd-rebound__putback"
                                @click="putBackPending()"
                            >
                                <v-icon color="green darken-1">mdi-backup-restore</v-icon>
                                <span class="sd-rebound__name">
                                    Put back
                                    <small>{{ playerLabel(pendingMiss.player) }} rebounds and scores {{ pendingMiss.category === '3pts' ? '3PT' : '2PT' }}</small>
                                </span>
                            </button>
                            <button
                                v-for="player in (side === 'dreb' ? (currentTeam === 1 ? team2SelectedPlayers : team1SelectedPlayers) : pickPlayers)"
                                :key="`reb-${side}-${player.id}`"
                                type="button"
                                class="sd-rebound__player"
                                :class="{ 'sd-pick-tile--unavailable': !isAvailable(player) }"
                                @click="pickRebound(side, player)"
                            >
                                <span class="sd-rebound__jersey" :style="teamStyle(side === 'dreb' ? opponentColor : currentColor)">{{ jerseyOf(player) }}</span>
                                <span class="sd-rebound__photo">
                                    <v-icon color="grey"> mdi-account </v-icon>
                                </span>
                                <span class="sd-rebound__name">{{ player.name }} <small>{{ player.surname }}</small></span>
                            </button>
                            <button type="button" class="sd-rebound__player sd-rebound__team" @click="pickRebound(side, null)">
                                <v-icon>mdi-account-group</v-icon>Team rebound
                            </button>
                        </div>
                    </div>
                    <v-btn large text block class="sd-btn mt-2" @click="noRebound()">
                        <v-icon left>mdi-skip-next</v-icon>No rebound
                    </v-btn>
                </div>

                <div v-else-if="choice === null && pointsDialog" class="sd-choice">
                    <div class="sd-choice__prompt">Shot result</div>
                    <v-btn class="sd-choice__btn" depressed color="green" dark @click="setChoice('made')">
                        <v-icon left>mdi-check-bold</v-icon>Made
                    </v-btn>
                    <v-btn class="sd-choice__btn sd-choice__btn--missed" depressed outlined color="red darken-1" @click="setChoice('missed')">
                        <v-icon left>mdi-close-thick</v-icon>Missed
                    </v-btn>
                </div>

                <div v-else-if="choice === null && foulsDialog" class="sd-choice">
                    <div class="sd-choice__prompt">Foul type</div>
                    <v-btn class="sd-choice__btn" depressed color="primary" dark @click="setChoice('personal')">
                        <v-icon left>mdi-whistle-outline</v-icon>Personal
                    </v-btn>
                    <!-- FIBA 2026: a technical of category 1 (conduct) counts towards a game disqualification, one of
                         category 2 (administrative: delay, hanging on the ring) does not; both are team fouls -->
                    <div class="sd-choice__row">
                        <v-btn class="sd-choice__btn sd-choice__btn--half" depressed color="red darken-1" dark @click="setChoice('technical')">
                            <span class="sd-choice__label">Technical T1<small>conduct · counts to DQ</small></span>
                        </v-btn>
                        <v-btn class="sd-choice__btn sd-choice__btn--half" depressed color="red lighten-1" dark @click="setChoice('technical2')">
                            <span class="sd-choice__label">Technical T2<small>delay, ring · no DQ</small></span>
                        </v-btn>
                    </div>
                    <!-- they replace the unsportsmanlike foul: the disruptive one does not count towards a game
                         disqualification, the flagrant one does -->
                    <div class="sd-choice__row">
                        <v-btn class="sd-choice__btn sd-choice__btn--half" depressed color="grey darken-2" dark @click="setChoice('disruptive')">
                            <span class="sd-choice__label">Disruptive (DI)<small>tactical stop · no DQ</small></span>
                        </v-btn>
                        <v-btn class="sd-choice__btn sd-choice__btn--half" depressed color="black" dark @click="setChoice('flagrant')">
                            <span class="sd-choice__label">Flagrant (FL)<small>dangerous · counts to DQ</small></span>
                        </v-btn>
                    </div>
                    <!-- the team's own technicals: on no player, and not team fouls -->
                    <div class="sd-choice__prompt sd-choice__prompt--team">{{ currentTeamName }} · not a team foul</div>
                    <div class="sd-choice__team">
                        <v-btn class="sd-choice__btn sd-choice__btn--team" depressed outlined color="red darken-1" @click="pickTeamFoul('coachTechnical')">
                            <v-icon left>mdi-account-tie</v-icon>Coach tech. (C)
                        </v-btn>
                        <v-btn class="sd-choice__btn sd-choice__btn--team" depressed outlined color="red darken-1" @click="pickTeamFoul('benchTechnical')">
                            <v-icon left>mdi-sofa-single-outline</v-icon>Bench tech. (B)
                        </v-btn>
                    </div>
                </div>

                <div v-else class="sd-pick-wrap">
                    <div class="sd-section-label">Pick the player</div>
                    <!-- at most 5 players: they share the whole screen as big tiles -->
                    <div
                        class="sd-pick-tiles"
                        :class="orientation === 'landscape' ? 'sd-pick-tiles--row' : 'sd-pick-tiles--column'"
                        :style="orientation === 'landscape'
                            ? { gridTemplateColumns: `repeat(${Math.max(pickPlayers.length + pickExtras.length, 1)}, minmax(0, 1fr))` }
                            : { gridTemplateRows: `repeat(${Math.max(pickPlayers.length + pickExtras.length, 1)}, minmax(0, 1fr))` }"
                    >
                        <button
                            v-for="player in pickPlayers"
                            :key="player.id"
                            type="button"
                            class="sd-pick-tile"
                            :class="{ 'sd-pick-tile--unavailable': !isAvailable(player) }"
                            @click="pickPlayer(player)"
                        >
                            <span class="sd-pick-tile__jersey" :style="teamStyle(currentColor)">{{ jerseyOf(player) }}</span>
                            <span class="sd-pick-tile__photo">
                                <v-icon x-large color="grey"> mdi-account </v-icon>
                            </span>
                            <span class="sd-pick-tile__name">
                                <span class="sd-pick-tile__first">{{ player.name }}</span>
                                <span class="sd-pick-tile__last">{{ player.surname }}</span>
                                <span v-if="!isAvailable(player)" class="sd-pick-tile__reason">
                                    <v-icon x-small color="red"> mdi-alert-circle </v-icon>
                                    {{ unavailableReasons(player).join(', ') }}
                                </span>
                            </span>
                        </button>
                        <button
                            v-for="extra in pickExtras"
                            :key="`extra-${extra.key}`"
                            type="button"
                            class="sd-pick-tile sd-pick-tile--extra"
                            :class="`sd-pick-tile--${extra.key}`"
                            @click="extra.key === 'team' ? pickTeam() : closeStatDialog()"
                        >
                            <v-icon x-large>{{ extra.icon }}</v-icon>
                            <span class="sd-pick-tile__extra-label">{{ extra.label }}</span>
                        </button>
                        <div v-if="!pickPlayers.length && !pickExtras.length" class="sd-empty">No players on court</div>
                    </div>
                </div>

                <div class="sd-footer">
                    <v-btn text large block class="sd-btn" @click="closeStatDialog()">
                        Close
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="retry"
            max-width="380px"
            persistent
        >
            <v-card class="sd-card">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon color="red" class="mr-2">mdi-wifi-alert</v-icon>The stat wasn't saved
                </v-card-title>
                <v-card-text class="text-body-2">
                    Something went wrong while saving. Check the connection and try again.
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text large class="flex-grow-1 sd-btn" @click="retry=false; dialog = false; pointsDialog = false; foulsDialog = false; reboundDialog = false; playersDialog = false; overlay = false">
                        Close
                    </v-btn>
                    <v-btn depressed large color="black" class="flex-grow-1 white--text sd-btn" @click="setStat(selectedPlayer)">
                        <v-icon left>mdi-refresh</v-icon>Retry
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="shootingChartDialog"
            hide-overlay
            persistent
            transition="dialog-bottom-transition"
            class="mx-2"
            :fullscreen="true"
        >
            <v-card class="sd-fullscreen">
                <stats-dialog-bar
                    title="Shot location"
                    :subtitle="`${currentTeamName} · ${choice === 'made' ? 'made' : 'missed'} ${category === '3pts' ? '3PT' : (category === '2pts' ? '2PT' : 'shot')}`"
                    :color="currentColor"
                    :text-color="textOn(currentColor)"
                    @close="shootingChartDialog = false; closeStatDialog();"
                />
                <div v-if="isSelectMode" class="sd-hint">
                    Tap where the shot was taken (2PT or 3PT is read from the spot){{ choice === 'made' ? ' — it saves straight away' : ' — then pick the rebound' }}
                    <div class="mt-2">
                        <v-btn outlined small class="sd-btn" @click="saveFreeThrow()">
                            <v-icon left small>mdi-numeric-1-circle-outline</v-icon>Free throw {{ choice === 'made' ? 'made' : 'missed' }}
                        </v-btn>
                    </div>
                </div>
                <div v-else class="sd-hint">
                    Tap where the shot was taken, {{ category === '3pts' ? 'outside' : 'inside' }} the 3PT line{{ choice === 'made' ? ' — it saves straight away' : ' — then pick the rebound' }}
                </div>
                <shooting-chart
                    v-if="shootingChartDialog"
                    class="mt-2"
                    :type="isSelectMode ? null : category"
                    :made="choice === 'made'"
                    :shooting-id="`${category}-${choice}-${selectedPlayer ? selectedPlayer.id : 'team'}`"
                    @setCategory="category = $event"
                    @updateShots="onShotPlaced"
                />
            </v-card>
        </v-dialog>

        <!-- advanced stats of a shot: after its location (new shot), or from the feed (editing) -->
        <v-dialog
            v-model="shotDetailsDialog"
            hide-overlay
            persistent
            transition="dialog-bottom-transition"
            :fullscreen="true"
        >
            <v-card class="sd-fullscreen">
                <stats-dialog-bar
                    title="Shot details"
                    :subtitle="shotDetailsSubtitle"
                    :color="shotDetailsEdit ? '#000000' : currentColor"
                    :text-color="shotDetailsEdit ? '#ffffff' : textOn(currentColor)"
                    @close="skipShotDetails"
                />
                <stats-shot-details
                    v-if="shotDetailsDialog"
                    :value="shotDetailsStart"
                    :editing="!!shotDetailsEdit"
                    :color="shotDetailsEdit ? '#000000' : currentColor"
                    :text-color="shotDetailsEdit ? '#ffffff' : textOn(currentColor)"
                    @save="saveShotDetails"
                    @skip="skipShotDetails"
                />
            </v-card>
        </v-dialog>

        <v-dialog v-model="timerDialog" max-width="360px" persistent>
            <v-card class="sd-card">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon color="primary" class="mr-2">mdi-timer-edit-outline</v-icon>Set the clock
                </v-card-title>
                <v-card-text>
                    <div class="sd-timer">
                        <v-text-field
                            v-model="newMinutes"
                            class="sd-timer__input"
                            type="number"
                            min="0"
                            label="Min"
                            outlined
                            hide-details
                            :placeholder="minutes"
                        />
                        <span class="sd-timer__colon">:</span>
                        <v-text-field
                            v-model="newSeconds"
                            class="sd-timer__input"
                            type="number"
                            min="0"
                            max="59"
                            label="Sec"
                            outlined
                            hide-details
                            :placeholder="seconds"
                        />
                    </div>
                    <div class="sd-timer__presets">
                        <v-chip v-for="preset in [10, 5, 1]" :key="preset" small outlined @click="newMinutes = preset; newSeconds = 0">
                            {{ preset }}:00
                        </v-chip>
                    </div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text large class="flex-grow-1 sd-btn" @click="timerDialog = false; newMinutes = null; newSeconds = null">
                        Cancel
                    </v-btn>
                    <v-btn depressed large color="black" class="flex-grow-1 white--text sd-btn" @click="timerDialog = false; changeTimer()">
                        Apply
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="feedDialog" max-width="720px" scrollable>
            <v-card class="sd-card">
                <stats-dialog-bar title="Feed" :color="'#000000'" :text-color="'#ffffff'" @close="feedDialog = false" />
                <div class="pa-3">
                    <stats-feed v-bind="feedProps" :height="feedDialogHeight" v-on="feedListeners" />
                </div>
            </v-card>
        </v-dialog>
        <v-dialog v-model="teamStatsDialog" max-width="520px">
            <v-card class="sd-card">
                <stats-dialog-bar title="Team stats" :color="'#000000'" :text-color="'#ffffff'" @close="teamStatsDialog = false" />
                <div class="pa-3">
                    <stats-team-stats :rows="teamStatRows" :color1="matchColorOf(match.team1)" :color2="matchColorOf(match.team2)" />
                </div>
            </v-card>
        </v-dialog>
        <confirm-dialog
            v-model="deleteDialog"
            title="Delete this event?"
            text="The event is removed from the feed."
            confirm-text="Delete"
            icon="mdi-trash-can-outline"
            @confirm="deleteLog()"
        />
        <confirm-dialog
            v-model="deleteAllLogsDialog"
            title="Delete the whole feed?"
            text="Every event of this match is removed. This can't be undone."
            confirm-text="Delete all"
            @confirm="deleteAllLogs()"
        />
        <confirm-dialog
            v-model="endMatchDialog"
            title="End the match?"
            text="This finishes the match and takes you back to the previous page."
            confirm-text="End match"
            icon="mdi-flag-checkered"
            @confirm="endMatch()"
        />
        <!-- Snackbar for success/error messages -->
        <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
            {{ snackbarText }}
            <v-btn text @click="showSnackbar = false">Close</v-btn>
        </v-snackbar>
    </v-container>
</template>

<script>
    import matchService from '@/services/match'
    import playerService from '@/services/player'
    import momentTimezone from 'moment-timezone'
    import teamService from '@/services/team'
    import ShootingChart from '@/components/ShootingChart.vue'
    import ConfirmDialog from '@/components/ConfirmDialog'
    import StatsDialogBar from '@/components/StatsDialogBar'
    import QrScanner from '@/components/QrScanner'
    import StatsPlayerRow from '@/components/StatsPlayerRow'
    import StatsPortrait from '@/components/stats/StatsPortrait'
    import StatsLandscape from '@/components/stats/StatsLandscape'
    import StatsClock from '@/components/stats/StatsClock'
    import StatsFeed from '@/components/stats/StatsFeed'
    import StatsSelectPortrait from '@/components/stats/StatsSelectPortrait'
    import StatsSelectLandscape from '@/components/stats/StatsSelectLandscape'
    import StatsTeamStats from '@/components/stats/StatsTeamStats'
    import StatsJerseyEdit from '@/components/stats/StatsJerseyEdit'
    import StatsShotDetails from '@/components/stats/StatsShotDetails'
    import { emptyShotDetails, shotDetailsOf, hasShotDetails, describeShotDetails } from '@/components/stats/shotDetails'

    // every foul of a player, all of them team fouls (the unsportsmanlike one before FIBA 2026, the disruptive and
    // the flagrant one after)
    const FOUL_KEYS = ['personalFouls', 'technicalFouls', 'unsportsmanlikeFouls', 'disruptiveFouls', 'flagrantFouls']

    const ADVANCED_STATS_KEY = 'commercial-cms-stats-advanced'

    export default {
        components: {
            QrScanner,
            ShootingChart,
            ConfirmDialog,
            StatsDialogBar,
            StatsPlayerRow,
            StatsPortrait,
            StatsLandscape,
            StatsClock,
            StatsFeed,
            StatsSelectPortrait,
            StatsSelectLandscape,
            StatsTeamStats,
            StatsJerseyEdit,
            StatsShotDetails
        },

        data: () => {
            return {
                deleteAllLogsDialog: false,
                shootingChartDialog: false,
                shots: [],
                // advanced stats of the shots (remembered per device): a step after the shot location
                advancedStats: false,
                shotDetailsDialog: false,
                // the values the step opens with, and the shot location waiting for it (new shot)
                shotDetailsStart: null,
                shotDetailsShots: null,
                // the details picked for the shot being saved (sent with it, then cleared)
                shotDetails: null,
                // the feed log being edited ({ logId, text }), null for a new shot
                shotDetailsEdit: null,
                orientation: 'portrait',
                // layout picked with the view button ('portrait' | 'landscape'); cleared when the device is rotated
                orientationOverride: null,
                lastDeviceLandscape: null,
                retry: false,
                statDialog: false,
                deleteDialog: false,
                endMatchDialog: false,
                reboundDialog: false,
                dialog: false,
                playersDialog: false,
                matchDate: null,
                match: {
                    league: null,
                    cupLeague: null,
                    team1: {
                        id: null,
                        name: '',
                        color: '#000000'
                    },
                    gameRosterTeam1: [],
                    team2: {
                        id: null,
                        name: '',
                        color: '#000000'
                    },
                    gameRosterTeam2: []
                },
                googleApiHost: 'https://storage.googleapis.com',
                // the game clock: while it runs, the time left is worked out from when it was started
                // (an interval that counts down would stop with the screen off or the app in the background)
                running: false,
                clockStartedAt: 0,
                clockStartValue: 0,
                // set once the clock has been restored on opening: until then nothing is sent to the live stream
                clockReady: false,
                // whether the last clock event reached the server: 'ok' | 'pending' | 'failed'
                clockSync: 'ok',
                clockEventSeq: 0,
                team1Players: [],
                team1SelectedPlayers: [],
                team2Players: [],
                team2SelectedPlayers: [],
                currentTeam: null,
                team1: null,
                team1Fouls: 0,
                team2: null,
                team2Fouls: 0,
                pointsDialog: false,
                foulsDialog: false,
                choice: null,
                category: null,
                timer: 600000,
                newMinutes: null,
                newSeconds: null,
                timerDialog: false,
                clockInterval: null,
                feed: [],
                logId: null,
                quarter: 1,
                selectedPlayer: null,
                overlay: false,
                // a stat request is in flight: taps are blocked (no dimming) and the scoreboard says "Saving…"
                saving: false,
                // confirmation after each stat, with Undo
                statToast: false,
                statToastText: '',
                statToastLogIds: [],
                // grey informational toast (undone / shot cancelled), no Undo button
                statToastNeutral: false,
                undoing: false,
                // feed ids before the last action, to find the log(s) it created
                feedSnapshot: null,
                lastStatPlayerId: null,
                // substitutions: player picked to come off, swapped with the next player sent in
                swapOutPlayer: null,
                batchNextStat: false,
                // a missed 2PT/3PT waits on the rebound screen and is saved with the choice made there
                pendingMiss: null,
                // 'pick': stat first, then the player · 'select': player or team first, then the stat (remembered per device)
                entryMode: 'pick',
                feedDialog: false,
                teamStatsDialog: false,
                matchStats: {
                    'team1': {
                        'name': '',
                        'players': []
                    },
                    'team2': {
                        'name': '',
                        'players': []
                    }
                },
                stats: [
                    { text: 'Pts', value: 'points', team1: { player: null, points: 0 }, team2: { player: null, points: 0 } },
                    { text: '2P%', value: 'two_points_percentage', team1: { player: null, twoPointAttempts: 0, twoPointsMade: 0, two_points_percentage: 0 }, team2: { player: null, twoPointAttempts: 0, twoPointsMade: 0, two_points_percentage: 0 } },
                    { text: '3P%', value: 'three_points_percentage', team1: { player: null, threePointAttempts: 0, threePointsMade: 0, three_points_percentage: 0 }, team2: { player: null, threePointAttempts: 0, threePointsMade: 0, three_points_percentage: 0 } },
                    { text: 'FT%', value: 'free_throw_percentage', team1: { player: null, freeThrowAttempts: 0, freeThrowsMade: 0, free_throw_percentage: 0 }, team2: { player: null, freeThrowAttempts: 0, freeThrowsMade: 0, free_throw_percentage: 0 } },
                    { text: 'OR', value: 'offensiveRebounds', team1: { player: null, offensiveRebounds: 0 }, team2: { player: null, offensiveRebounds: 0 } },
                    { text: 'DR', value: 'defensiveRebounds', team1: { player: null, defensiveRebounds: 0 }, team2: { player: null, defensiveRebounds: 0 } },
                    { text: 'TR', value: 'rebounds', team1: { player: null, rebounds: 0 }, team2: { player: null, rebounds: 0 } },
                    { text: 'AST', value: 'assists', team1: { player: null, assists: 0 }, team2: { player: null, assists: 0 } },
                    { text: 'STL', value: 'steals', team1: { player: null, steals: 0 }, team2: { player: null, steals: 0 } },
                    { text: 'TO', value: 'turnovers', team1: { player: null, turnovers: 0 }, team2: { player: null, turnovers: 0 } },
                    { text: 'BLK', value: 'blockedShots', team1: { player: null, blockedShots: 0 }, team2: { player: null, blockedShots: 0 } },
                    { text: 'FC', value: 'personalFouls', team1: { player: null, personalFouls: 0 }, team2: { player: null, personalFouls: 0 } },
                    { text: 'TFC', value: 'technicalFouls', team1: { player: null, technicalFouls: 0 }, team2: { player: null, technicalFouls: 0 } },
                    { text: 'USF', value: 'unsportsmanlikeFouls', team1: { player: null, unsportsmanlikeFouls: 0 }, team2: { player: null, unsportsmanlikeFouls: 0 } },
                    { text: 'DIF', value: 'disruptiveFouls', team1: { player: null, disruptiveFouls: 0 }, team2: { player: null, disruptiveFouls: 0 } },
                    { text: 'FLF', value: 'flagrantFouls', team1: { player: null, flagrantFouls: 0 }, team2: { player: null, flagrantFouls: 0 } }
                ],
                made: false,
                mvp: { id: null, points: 0 },
                // snackbar for user feedback
                showSnackbar: false,
                // the QR scanner of the game roster, and what came of the last card
                rosterScanner: false,
                rosterScanStatus: null,
                snackbarText: '',
                snackbarColor: 'success',
                feedFilter: null,
                // feed time filter: operator ('=' | '>=' | '<=') and a game time typed as m, mm:ss
                feedTimeOp: '=',
                feedTime: null,
                unavailableDialog: false,
                colorDialog: false,
                colorDrafts: [],
                colorSaving: false,
                unavailablePlayer: null,
                doctorCheckLoading: false,
                playoffEligibility: {},
                activePenalties: {},
                team1BenchPanel: undefined,
                team2BenchPanel: undefined,
                gameRosterDialog: false
            }
        },
        computed: {
            // colour of the team the open dialog is about, and of the other team
            currentColor() {
                return this.teamColor(this.currentTeam === 1 ? 1 : 2)
            },
            currentTeamName() {
                return this.currentTeam === 1 ? this.match.team1.name : (this.currentTeam === 2 ? this.match.team2.name : '')
            },
            // the two columns of the rebound screen as the teams sit on the court: team 1 on the left, whichever
            // team shot (defensive = the other team of the shooter's, offensive = his own)
            reboundSides() {
                return this.currentTeam === 1 ? ['oreb', 'dreb'] : ['dreb', 'oreb']
            },
            // players on court of the team the dialog is about (the "pick the player" step)
            pickPlayers() {
                const players = this.currentTeam === 1 ? this.team1SelectedPlayers : this.team2SelectedPlayers
                // the shooter can't assist his own basket
                return this.category === 'asst' && this.lastStatPlayerId
                    ? players.filter(player => player.id !== this.lastStatPlayerId)
                    : players
            },
            // extra tiles next to the players in the pick step
            pickExtras() {
                const extras = []
                if (['dreb', 'oreb', 'to'].includes(this.category)) {
                    extras.push({ key: 'team', icon: 'mdi-account-group', label: this.category === 'to' ? 'Team turnover' : 'Team rebound' })
                }
                if (this.category === 'asst') {
                    extras.push({ key: 'skip', icon: 'mdi-close-circle-outline', label: 'No assist' })
                } else if (this.category === 'to') {
                    extras.push({ key: 'skip', icon: 'mdi-skip-next', label: 'Skip' })
                }
                return extras
            },
            currentTeamPlayers() {
                return this.currentTeam === 1 ? this.team1Players : this.team2Players
            },
            opponentColor() {
                return this.teamColor(this.currentTeam === 1 ? 2 : 1)
            },
            phaseLabel() {
                return this.match.phase === 'regular_season' ? 'Regular Season' : (this.match.phase === 'placements' ? 'Προκριματικά' : 'Play Offs')
            },
            quarterLabel() {
                const item = this.quarterItems.find(quarterItem => quarterItem.value === this.quarter)
                return item ? item.text : '-'
            },
            isSelectMode() {
                return this.entryMode === 'select'
            },
            // select-first mode: who the next stat goes to
            selection() {
                return { side: this.currentTeam, playerId: this.selectedPlayer ? this.selectedPlayer.id : null }
            },
            selectActionColor() {
                return this.currentTeam ? this.currentColor : '#9e9e9e'
            },
            // stats that need a player vs the ones a team can take too
            selectActions() {
                const player = !!this.selectedPlayer
                const team = !!this.currentTeam
                return [
                    { key: 'made', label: 'Made shot', icon: 'mdi-check-bold', enabled: player },
                    { key: 'missed', label: 'Missed shot', icon: 'mdi-close-thick', enabled: player },
                    { key: 'sub', label: 'Sub', icon: 'mdi-swap-horizontal', enabled: team },
                    { key: 'foul', label: 'Foul', icon: 'mdi-whistle-outline', enabled: team },
                    { key: 'dreb', label: 'Def. reb', icon: 'mdi-shield-outline', enabled: team },
                    { key: 'oreb', label: 'Off. reb', icon: 'mdi-arrow-up-bold-outline', enabled: team },
                    { key: 'asst', label: 'Assist', icon: 'mdi-handshake-outline', enabled: player },
                    { key: 'stl', label: 'Steal', icon: 'mdi-hand-back-right-outline', enabled: player },
                    { key: 'blk', label: 'Block', icon: 'mdi-cancel', enabled: player },
                    { key: 'to', label: 'Turnover', icon: 'mdi-alert-circle-outline', enabled: team }
                ]
            },
            feedDialogHeight() {
                return Math.max(240, Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.65))
            },
            // everything the portrait / landscape layouts show about one team, side 1 and side 2
            teamViews() {
                return [1, 2].map(side => {
                    const teamId = side === 1 ? this.team1 : this.team2
                    const color = this.teamColor(side)
                    return {
                        side,
                        name: side === 1 ? this.match.team1.name : this.match.team2.name,
                        color,
                        textColor: this.textOn(color),
                        light: this.isLightColor(color),
                        court: (side === 1 ? this.team1SelectedPlayers : this.team2SelectedPlayers).map(player => {
                            const fouls = this.getFouls(player, teamId)
                            const disqualified = this.getDisqualifyingFouls(player, teamId) >= 2
                            return {
                                player,
                                jersey: this.jerseyOf(player),
                                points: this.getPoints(player, teamId),
                                rebounds: this.getRebounds(player, teamId),
                                assists: this.getAssists(player, teamId),
                                fouls,
                                disqualified,
                                foulClass: this.foulClass(fouls, disqualified),
                                mvp: this.mvp.id === player.id
                            }
                        }),
                        bench: this.benchPlayers(side).map(player => ({
                            player,
                            jersey: this.jerseyOf(player),
                            available: this.isAvailable(player),
                            reasons: this.unavailableReasons(player)
                        }))
                    }
                })
            },
            feedRows() {
                return this.filterFeed().map(log => (log.matchEnded ? {
                    // the end of game flag (deleting it opens the match again)
                    logId: log.logId,
                    timestamp: log.timestamp,
                    text: 'end of game',
                    logo: null,
                    teamStyle: this.teamStyle('#0d0d0f'),
                    status: 'ended'
                } : {
                    logId: log.logId,
                    timestamp: log.timestamp,
                    text: this.getLogText(log),
                    logo: this.getTeamLogo(log),
                    teamStyle: this.teamStyle(this.teamColor(log.teamId === this.team1 ? 1 : 2)),
                    status: log.playerMatchStatus,
                    // a 2PT / 3PT shot can get advanced stats (the tag is shown while they are on)
                    shot: this.advancedStats && this.isFieldGoalLog(log),
                    detailsText: describeShotDetails(shotDetailsOf(log))
                }))
            },
            shotDetailsSubtitle() {
                if (this.shotDetailsEdit) return this.shotDetailsEdit.text
                const shot = this.category === '3pts' ? '3PT' : '2PT'
                const who = this.selectedPlayer ? `#${this.jerseyOf(this.selectedPlayer)} ${this.selectedPlayer.surname || ''}` : this.currentTeamName
                return `${who} · ${this.choice === 'made' ? 'made' : 'missed'} ${shot}`
            },
            feedProps() {
                return {
                    rows: this.feedRows,
                    jersey: this.feedFilter,
                    timeOp: this.feedTimeOp,
                    time: this.feedTime,
                    timeInvalid: !!this.feedTime && !this.feedTimeRange,
                    filtersActive: this.feedFiltersActive
                }
            },
            feedListeners() {
                return {
                    'update:jersey': value => { this.feedFilter = value },
                    'update:timeOp': value => { this.feedTimeOp = value },
                    'update:time': value => { this.feedTime = value },
                    'delete-log': logId => {
                        this.logId = logId
                        this.deleteDialog = true
                    },
                    'delete-all': () => { this.deleteAllLogsDialog = true },
                    'edit-shot': logId => this.openShotDetailsEdit(logId)
                }
            },
            // typed feed time as a range in seconds: "12" -> 12:00..12:59, "12:30" -> 12:30..12:30
            feedTimeRange() {
                const value = (this.feedTime || '').trim()
                const match = value.match(/^(\d{1,3})(?::(\d{1,2}))?$/)
                if (!match) return null
                const minutes = parseInt(match[1], 10)
                if (match[2] === undefined) {
                    return { from: minutes * 60, to: minutes * 60 + 59 }
                }
                const seconds = parseInt(match[2], 10)
                if (seconds > 59) return null
                return { from: minutes * 60 + seconds, to: minutes * 60 + seconds }
            },
            feedFiltersActive() {
                return !!this.feedFilter || !!this.feedTimeRange
            },
            // quarters 5+ are overtimes
            quarterItems() {
                return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(value => ({ value, text: value <= 4 ? `Q${value}` : `OT${value - 4}` }))
            },
            // team totals for the landscape comparison, read from the stats the page already computes
            teamStatRows() {
                const rows = [
                    { key: 'rebounds', label: 'Rebounds' },
                    { key: 'assists', label: 'Assists' },
                    { key: 'steals', label: 'Steals' },
                    { key: 'blockedShots', label: 'Blocks' },
                    { key: 'turnovers', label: 'Turnovers' },
                    { key: 'fouls', label: 'Fouls' },
                    { key: 'technicalFouls', label: 'Tech. fouls' },
                    { key: 'disruptiveFouls', label: 'Disruptive (DI)' },
                    { key: 'flagrantFouls', label: 'Flagrant (FL)' },
                    // before FIBA 2026 only
                    { key: 'unsportsmanlikeFouls', label: 'Unsp. fouls', old: true }
                ]
                const value = (key, side) => {
                    const stat = (this.stats || []).find(element => element.value === key)
                    return stat && stat[side] ? stat[side][key] || 0 : 0
                }
                // the team's own technicals are on no player: counted from the logs
                const teamLogs = (key, side) => (this.feed || [])
                    .filter(log => log.teamId === (side === 'team1' ? this.team1 : this.team2))
                    .reduce((total, log) => total + (log[key] || 0), 0)
                const result = rows.map(row => {
                    const side = team => (row.key === 'fouls'
                        ? FOUL_KEYS.reduce((total, key) => total + value(key, team), 0)
                        : value(row.key, team))
                    return { label: row.label, team1: side('team1'), team2: side('team2'), old: row.old }
                }).filter(row => !row.old || row.team1 || row.team2)
                return result.concat([
                    { key: 'coachTechnicalFouls', label: 'Coach tech. (C)' },
                    { key: 'benchTechnicalFouls', label: 'Bench tech. (B)' }
                ].map(row => ({ label: row.label, team1: teamLogs(row.key, 'team1'), team2: teamLogs(row.key, 'team2') })))
            },
            // which team's bench is expanded in portrait (1, 2 or null)
            openBenchSide() {
                if (this.team1BenchPanel === 0) return 1
                if (this.team2BenchPanel === 0) return 2
                return null
            },
            // an open bench takes the whole width (both benches stack)
            benchExpanded() {
                return this.team1BenchPanel === 0 || this.team2BenchPanel === 0
            },
            minutes() {
                let temp = Math.floor(this.getSafeTimer() / 60000)
                return (temp < 10 ? '0' : '') + temp
            },
            seconds() {
                let temp = Math.floor(((this.getSafeTimer() % 60000) / 1000))
                return (temp < 10 ? '0' : '') + temp
            },
            team1BenchPlayers() {
                return this.team1Players.filter(p =>
                    !this.team1SelectedPlayers.some(sel => sel.id === p.id) &&
                    (this.match.gameRosterTeam1.length === 0 || this.match.gameRosterTeam1.includes(p.id)))
            },
            team2BenchPlayers() {
                return this.team2Players.filter(p =>
                    !this.team2SelectedPlayers.some(sel => sel.id === p.id) &&
                    (this.match.gameRosterTeam2.length === 0 || this.match.gameRosterTeam2.includes(p.id)))
            }
        },
        watch: {
            match() {
                this.getFeed()
                this.getMatchStats()
            },
            // A new period (picked, or the clock ran out): its full time on a stopped clock
            quarter() {
                this.setQuarterFouls()
                if (!this.clockReady) return
                this.setClockValue(this.getPeriodDurationMs(this.quarter))
                this.pauseTimer()
            }
        },
        beforeCreate() {
        },
        mounted() {
            window.addEventListener('resize', this.updateOrientation)
            window.addEventListener('orientationchange', this.updateOrientation)
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.updateOrientation)
            window.removeEventListener('orientationchange', this.updateOrientation)
            document.removeEventListener('visibilitychange', this.tickClock)
            clearInterval(this.clockInterval)
        },
        created() {
            this.loadEntryMode()
            this.advancedStats = localStorage.getItem(ADVANCED_STATS_KEY) === 'true'
            this.updateOrientation()
            // back on screen: the clock catches up at once
            document.addEventListener('visibilitychange', this.tickClock)
            this.getMatch().then(() => {
                this.getPlayoffEligibility()
                this.getActivePenalties()
                this.restoreClock()
            })
        },
        methods: {
            teamColor(side) {
                return this.matchColorOf(side === 1 ? this.match.team1 : this.match.team2)
            },
            // the match colour of a team (its brand colour), else its team colour
            matchColorOf(team) {
                const brand = String((team && team.brandColor) || '').toLowerCase()
                return /^#[0-9a-f]{6}$/.test(brand) ? brand : (team ? team.color : null)
            },
            openColorDialog() {
                this.colorDrafts = [this.match.team1, this.match.team2].map((team, index) => {
                    const own = this.matchColorOf(team) !== team.color ? this.matchColorOf(team) : null
                    return { side: index + 1, team, color: own, initial: own }
                })
                this.colorDialog = true
            },
            // the team colour first, then colours that read well on the buttons
            colorSwatches(team) {
                const swatches = [team.color, '#d32f2f', '#c2185b', '#7b1fa2', '#303f9f', '#1976d2', '#0288d1', '#00897b',
                                  '#388e3c', '#f9a825', '#ef6c00', '#5d4037', '#455a64', '#212121', '#ffffff']
                return [...new Set(swatches.filter(Boolean).map(color => String(color).toLowerCase()))]
            },
            saveMatchColors() {
                const changed = this.colorDrafts.filter(draft => draft.color !== draft.initial)
                this.colorSaving = true
                Promise.all(changed.map(draft => teamService.setMatchColor(draft.team.id, draft.color)
                    .then(() => this.$set(draft.team, 'brandColor', draft.color))))
                    .then(() => {
                        this.colorDialog = false
                    })
                    .catch(() => {
                        this.snackbarText = 'Could not save the match colour'
                        this.snackbarColor = 'error'
                        this.showSnackbar = true
                    })
                    .finally(() => {
                        this.colorSaving = false
                    })
            },
            // team colours come from the CMS, so pick black or white text by brightness
            isLightColor(color) {
                const hex = (color || '').replace('#', '')
                if (hex.length < 6) {
                    return false
                }
                const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.substr(i, 2), 16))
                return (r * 299 + g * 587 + b * 114) / 1000 > 160
            },
            textOn(color) {
                return this.isLightColor(color) ? 'rgba(0, 0, 0, 0.87)' : '#ffffff'
            },
            teamStyle(color) {
                return { backgroundColor: color, color: this.textOn(color) }
            },
            jerseyOf(player) {
                return player.alternativeJersey || player.alternativeJersey === 0 ? player.alternativeJersey : player.jersey
            },
            foulClass(fouls, disqualified) {
                if (fouls >= 5 || disqualified) return 'p-player__fouls--out'
                if (fouls === 4) return 'p-player__fouls--warn'
                return ''
            },
            benchPlayers(side) {
                return side === 1 ? this.team1BenchPlayers : this.team2BenchPlayers
            },
            benchPanelOpen(side) {
                return (side === 1 ? this.team1BenchPanel : this.team2BenchPanel) === 0
            },
            // only one bench open at a time
            toggleBench(side) {
                const open = this.benchPanelOpen(side)
                this.team1BenchPanel = side === 1 && !open ? 0 : undefined
                this.team2BenchPanel = side === 2 && !open ? 0 : undefined
            },
            inRoster(player) {
                return (this.currentTeam === 1 ? this.match.gameRosterTeam1 : this.match.gameRosterTeam2).includes(player.id)
            },
            openRosterScanner() {
                this.rosterScanStatus = null
                this.rosterScanner = true
            },
            // the QR of a health card holds the page of the player on the site: .../Player/<id>
            onRosterScan(text) {
                const found = /\/Player\/(\d+)/i.exec(String(text))
                if (!found) {
                    this.rosterScanStatus = { type: 'error', text: 'Not the QR of a health card' }
                    return
                }
                const id = Number(found[1])
                const player = this.currentTeamPlayers.find(item => Number(item.id) === id)
                if (!player) {
                    const other = (this.currentTeam === 1 ? this.team2Players : this.team1Players).find(item => Number(item.id) === id)
                    this.rosterScanStatus = { type: 'error', text: other ? `${this.playerLabel(other)} plays for the other team` : `Player ${id} is not in ${this.currentTeamName}` }
                    return
                }
                if (this.inRoster(player)) {
                    this.rosterScanStatus = { type: 'info', text: `${this.playerLabel(player)} is already in the roster` }
                    return
                }
                if (!this.isAvailable(player)) {
                    this.rosterScanStatus = { type: 'error', text: `${this.playerLabel(player)}: ${this.unavailableReasons(player).join(' · ')}` }
                    return
                }
                this.addToRoster(player)
                this.rosterScanStatus = { type: 'success', text: `${this.playerLabel(player)} is in the roster` }
            },
            addToRoster(player) {
                this.currentTeam === 1 ? this.match.gameRosterTeam1.push(player.id) : this.match.gameRosterTeam2.push(player.id)
            },
            removeFromRoster(player) {
                if (this.currentTeam === 1) {
                    this.match.gameRosterTeam1 = this.match.gameRosterTeam1.filter(element => player.id !== element)
                } else {
                    this.match.gameRosterTeam2 = this.match.gameRosterTeam2.filter(element => player.id !== element)
                }
            },
            isOnCourt(player) {
                return !!(this.currentTeam === 1 ? this.team1SelectedPlayers : this.team2SelectedPlayers).find(element => player.id === element.id)
            },
            closeStatDialog() {
                if (this.pendingMiss) {
                    this.statToastText = 'Shot not recorded'
                    this.statToastNeutral = true
                    this.statToastLogIds = []
                    this.statToast = true
                }
                this.pendingMiss = null
                this.shotDetails = null
                if (this.isSelectMode) {
                    this.currentTeam = null
                }
                this.dialog = false
                this.choice = null
                this.pointsDialog = false
                this.foulsDialog = false
                this.reboundDialog = false
                this.category = null
                this.statDialog = false
                this.selectedPlayer = null
            },
            loadEntryMode() {
                // old /stats/match/alt/:id links arrive with ?view=select
                if (this.$route.query.view === 'select' || this.$route.query.view === 'pick') {
                    this.entryMode = this.$route.query.view
                    return
                }
                try {
                    this.entryMode = localStorage.getItem('commercial-cms-stats-entry') === 'select' ? 'select' : 'pick'
                } catch (e) {
                    this.entryMode = 'pick'
                }
            },
            setAdvancedStats(enabled) {
                this.advancedStats = enabled
                localStorage.setItem(ADVANCED_STATS_KEY, String(enabled))
            },
            isFieldGoalLog(log) {
                return !log.playerMatchStatus && !log.matchEnded &&
                    ((log.twoPointAttempts || 0) > 0 || (log.threePointAttempts || 0) > 0 || (log.twoPointsMade || 0) > 0 || (log.threePointsMade || 0) > 0)
            },
            // Facts the step can start from: right after an offensive rebound of the team, a second chance
            suggestShotDetails() {
                const details = emptyShotDetails()
                const teamId = this.currentTeam === 1 ? this.team1 : this.team2
                const last = (this.feed || []).reduce((latest, log) => (!latest || (log.createdAt || 0) > (latest.createdAt || 0) ? log : latest), null)
                if (last && last.teamId === teamId && (last.offensiveRebounds || 0) > 0 && !last.playerMatchStatus) {
                    details.situation = 'SECOND_CHANCE'
                }
                return details
            },
            // the details picked for the shot being saved, once (empty ones are not sent)
            takeShotDetails() {
                const details = this.shotDetails
                this.shotDetails = null
                if (!hasShotDetails(details)) return {}
                return Object.keys(details).reduce((data, key) => {
                    if (details[key]) data[key] = details[key]
                    return data
                }, {})
            },
            openShotDetailsEdit(logId) {
                const log = (this.feed || []).find(item => item.logId === logId)
                if (!log) return
                this.shotDetailsEdit = { logId, text: this.getLogText(log) }
                this.shotDetailsStart = shotDetailsOf(log)
                this.shotDetailsDialog = true
            },
            saveShotDetails(details) {
                this.shotDetailsDialog = false
                if (this.shotDetailsEdit) {
                    const { logId } = this.shotDetailsEdit
                    this.shotDetailsEdit = null
                    matchService.updateShotDetails(this.match.id, logId, details).then(() => {
                        this.getFeed()
                    }).catch(() => {
                        this.snackbarText = 'Could not save the shot details'
                        this.snackbarColor = 'error'
                        this.showSnackbar = true
                    })
                    return
                }
                this.shotDetails = details
                this.continueShot(this.shotDetailsShots)
            },
            // new shot: saved without details; editing: nothing changes
            skipShotDetails() {
                this.shotDetailsDialog = false
                if (this.shotDetailsEdit) {
                    this.shotDetailsEdit = null
                    return
                }
                this.shotDetails = null
                this.continueShot(this.shotDetailsShots)
            },
            // switching mode drops whatever was half-way (open steps, selection)
            setEntryMode(mode) {
                if (mode === this.entryMode) return
                this.shootingChartDialog = false
                this.playersDialog = false
                this.swapOutPlayer = null
                this.closeStatDialog()
                this.currentTeam = null
                this.selectedPlayer = null
                this.entryMode = mode
                try {
                    localStorage.setItem('commercial-cms-stats-entry', mode)
                } catch (e) {
                    // storage unavailable: the choice just lasts for this visit
                }
            },
            // ---------- select-first mode ----------
            onSelectPlayer(player, side) {
                if (!this.isAvailable(player)) {
                    this.openUnavailableDialog(player)
                    return
                }
                if (this.selectedPlayer && this.selectedPlayer.id === player.id) {
                    this.clearSelection()
                    return
                }
                this.currentTeam = side
                this.selectedPlayer = player
            },
            selectTeam(side) {
                if (this.currentTeam === side && !this.selectedPlayer) {
                    this.clearSelection()
                    return
                }
                this.currentTeam = side
                this.selectedPlayer = null
            },
            clearSelection() {
                this.currentTeam = null
                this.selectedPlayer = null
            },
            runSelectAction(key) {
                if (key === 'made' || key === 'missed') {
                    this.choice = key
                    this.category = null
                    this.shots = []
                    this.shootingChartDialog = true
                } else if (key === 'sub') {
                    // the selected player (if on court) is already marked as coming off
                    this.swapOutPlayer = this.selectedPlayer && this.isOnCourt(this.selectedPlayer) ? this.selectedPlayer : null
                    this.playersDialog = true
                } else if (key === 'foul') {
                    this.category = 'foul'
                    this.statDialog = false
                    this.foulsDialog = true
                    this.dialog = true
                } else {
                    this.category = key
                    this.setStat(this.selectedPlayer)
                }
            },
            saveFreeThrow() {
                this.shootingChartDialog = false
                this.category = '1pt'
                this.setStat(this.selectedPlayer)
            },
            // a player card on court or on the bench: open stat entry, or explain why he can't play
            onPlayerClick(player, side) {
                if (!this.isAvailable(player)) {
                    this.openUnavailableDialog(player)
                    return
                }
                this.dialog = true
                this.statDialog = true
                this.currentTeam = side
                this.selectedPlayer = player
            },
            // a shot button in the tablet layout: pick the player next
            onLandscapeShot({ side, made, category }) {
                this.dialog = true
                this.currentTeam = side
                this.made = made
                this.category = category
            },
            runQuickAction(category, side) {
                this.currentTeam = side
                if (category === 'sub') {
                    this.playersDialog = true
                    return
                }
                this.dialog = false
                this.category = category
                this.statDialog = false
                this.setStat(this.selectedPlayer)
            },
            isAvailable(player) {
                return !player || this.unavailableReasons(player).length === 0
            },
            unavailableReasons(player) {
                const reasons = []
                if (player.doctorCheck !== true) {
                    reasons.push('Doctor check is pending')
                }
                const eligibility = this.playoffEligibility[player.id]
                if (eligibility && eligibility.required && eligibility.appearances < eligibility.required) {
                    reasons.push(`Not eligible for playoffs: ${eligibility.appearances}/${eligibility.required} regular season matches`)
                }
                const penalties = this.activePenalties[player.id] || []
                penalties.forEach(penalty => {
                    const why = penalty.reason ? ` (${penalty.reason})` : ''
                    reasons.push(penalty.permanent
                        ? `Permanently banned${why}`
                        : `Suspended: ${penalty.served}/${penalty.matches} matches served${why}`)
                })
                return reasons
            },
            getActivePenalties() {
                matchService.getActivePenalties(this.match.id).then(resp => {
                    const penalties = {}
                    resp.data.forEach(element => {
                        penalties[element.playerId] = [...(penalties[element.playerId] || []), element]
                    })
                    this.activePenalties = penalties
                }).catch(() => {
                    this.snackbarText = 'Failed to load player penalties'
                    this.snackbarColor = 'error'
                    this.showSnackbar = true
                })
            },
            getPlayoffEligibility() {
                // the appearances rule only applies to playoff matches, not cup
                if (this.match.phase !== 'playoffs' || this.match.cup) {
                    this.playoffEligibility = {}
                    return
                }
                matchService.getPlayoffEligibility(this.match.id).then(resp => {
                    const eligibility = {}
                    resp.data.forEach(element => {
                        eligibility[element.playerId] = element
                    })
                    this.playoffEligibility = eligibility
                }).catch(() => {
                    this.snackbarText = 'Failed to load playoff eligibility'
                    this.snackbarColor = 'error'
                    this.showSnackbar = true
                })
            },
            openUnavailableDialog(player) {
                this.unavailablePlayer = player
                this.unavailableDialog = true
            },
            setDoctorCheck(player) {
                this.doctorCheckLoading = true
                playerService.setDoctorCheck(player.id).then(() => {
                    player.doctorCheck = true
                    this.unavailableDialog = false
                    this.unavailablePlayer = null
                    this.snackbarText = `Doctor check set for ${player.name} ${player.surname || ''}`.trim()
                    this.snackbarColor = 'success'
                    this.showSnackbar = true
                }).catch(() => {
                    this.snackbarText = 'Failed to set doctor check'
                    this.snackbarColor = 'error'
                    this.showSnackbar = true
                }).finally(() => {
                    this.doctorCheckLoading = false
                })
            },
            getPlayers() {
                const players = this.currentTeam === 1 ? this.team1Players : this.team2Players
                const roster = this.currentTeam === 1 ? this.match.gameRosterTeam1 : this.match.gameRosterTeam2
                if (roster.length === 0) {
                    return players
                }
                return players.filter(player => roster.includes(player.id))
            },
            saveGameRoster() {
                matchService.updateMatch(this.match.id, this.match).then(() => {
                    this.snackbarText = 'Game roster updated successfully'
                    this.snackbarColor = 'success'
                    this.showSnackbar = true
                }).catch(() => {
                    this.snackbarText = 'Game roster was not updated due to an error'
                    this.snackbarColor = 'error'
                    this.showSnackbar = true
                })
                this.gameRosterDialog = false
            },
            setGameRoster(team) {
                this.currentTeam = team
                this.gameRosterDialog = true
            },
            filterFeed() {
                let logs = this.feed
                if (this.feedFilter) {
                    logs = logs.filter(log => {
                        return log.alternativeJersey ? (log.alternativeJersey !== null && log.alternativeJersey !== undefined && log.alternativeJersey.toString() === this.feedFilter) : (log.jersey !== null && log.jersey !== undefined && log.jersey.toString() === this.feedFilter)
                    })
                }
                const range = this.feedTimeRange
                if (range) {
                    logs = logs.filter(log => {
                        const seconds = this.timestampSeconds(log.timestamp)
                        if (this.feedTimeOp === '>=') return seconds >= range.from
                        if (this.feedTimeOp === '<=') return seconds <= range.to
                        return seconds >= range.from && seconds <= range.to
                    })
                }
                return logs
            },
            timestampSeconds(timestamp) {
                const [minutes = '0', seconds = '0'] = (timestamp || '').split(':')
                return (parseInt(minutes, 10) || 0) * 60 + (parseInt(seconds, 10) || 0)
            },
            subOutAll() {
                if (this.currentTeam === 1) {
                    this.team1SelectedPlayers.forEach((player) => {
                        this.removePlayer(player)
                    })
                    this.team1SelectedPlayers = []
                } else {
                    this.team2SelectedPlayers.forEach((player) => {
                        this.removePlayer(player)
                    })
                    this.team2SelectedPlayers = []
                }
            },
            getPoints(player, team) {
                if (team === this.matchStats.team1.id) {
                    if (!this.matchStats || !this.matchStats.team1.players.filter(element => element.id === player.id)[0] || !this.matchStats.team1.players.filter(element => element.id === player.id)[0].stats[0]) {
                        return 0
                    }
                    return this.matchStats.team1.players.filter(element => element.id === player.id)[0].stats[0].points
                } else {
                    if (!this.matchStats || !this.matchStats.team2.players.filter(element => element.id === player.id)[0] || !this.matchStats.team2.players.filter(element => element.id === player.id)[0].stats[0]) {
                        return 0
                    }
                    return this.matchStats.team2.players.filter(element => element.id === player.id)[0].stats[0].points
                }
            },
            getRebounds(player, team) {
                if (team === this.matchStats.team1.id) {
                    if (!this.matchStats || !this.matchStats.team1.players.filter(element => element.id === player.id)[0] || !this.matchStats.team1.players.filter(element => element.id === player.id)[0].stats[0]) {
                        return 0
                    }
                    return this.matchStats.team1.players.filter(element => element.id === player.id)[0].stats[0].rebounds
                } else {
                    if (!this.matchStats || !this.matchStats.team2.players.filter(element => element.id === player.id)[0] || !this.matchStats.team2.players.filter(element => element.id === player.id)[0].stats[0]) {
                        return 0
                    }
                    return this.matchStats.team2.players.filter(element => element.id === player.id)[0].stats[0].rebounds
                }
            },
            getAssists(player, team) {
                if (team === this.matchStats.team1.id) {
                    if (!this.matchStats || !this.matchStats.team1.players.filter(element => element.id === player.id)[0] || !this.matchStats.team1.players.filter(element => element.id === player.id)[0].stats[0]) {
                        return 0
                    }
                    return this.matchStats.team1.players.filter(element => element.id === player.id)[0].stats[0].assists
                } else {
                    if (!this.matchStats || !this.matchStats.team2.players.filter(element => element.id === player.id)[0] || !this.matchStats.team2.players.filter(element => element.id === player.id)[0].stats[0]) {
                        return 0
                    }
                    return this.matchStats.team2.players.filter(element => element.id === player.id)[0].stats[0].assists
                }
            },
            // the box score of a player in this match, null when there is none yet
            playerMatchStats(player, team) {
                if (!this.matchStats) return null
                const side = team === this.matchStats.team1.id ? this.matchStats.team1 : this.matchStats.team2
                const found = side.players.find(element => element.id === player.id)
                return (found && found.stats[0]) || null
            },
            // every foul of a player, all of them team fouls
            getFouls(player, team) {
                const stats = this.playerMatchStats(player, team)
                return stats ? FOUL_KEYS.reduce((total, key) => total + (stats[key] || 0), 0) : 0
            },
            // FIBA 2026: the fouls towards a game disqualification (2 of them): technicals of category 1, flagrant
            // fouls, and the unsportsmanlike fouls of before
            getDisqualifyingFouls(player, team) {
                const stats = this.playerMatchStats(player, team)
                if (!stats) return 0
                return (stats.technicalFouls || 0) - (stats.category2TechnicalFouls || 0) + (stats.flagrantFouls || 0) + (stats.unsportsmanlikeFouls || 0)
            },
            getMatchTitle() {
                if (this.match.cupLeague) {
                    return this.match.cupLeague.title
                } else if (this.match.league) {
                    return this.match.league.title
                } else {
                    return 'Friendly'
                }
            },
            deleteAllLogs() {
                matchService.deleteAllLogs(this.match.id).then(() => {
                    this.deleteAllLogsDialog = false
                    this.getFeed()
                    this.getMatch()
                    this.getActivePlayers()
                }).catch(() => {
                    this.deleteAllLogsDialog = false
                })
            },
            updateShots(shots) {
                this.shots = shots
            },
            setQuarterFouls() {
                this.team1Fouls = this.feed.reduce((acc, log) => {
                    if ((this.quarter <= 4 ? (this.quarter - 1) * 10 : 3 * 10) <= parseInt(log.timestamp.split(':')[0]) && parseInt(log.timestamp.split(':')[0]) < this.quarter * 10 && this.team1 === log.teamId) {
                        return acc +
                            (typeof log.personalFouls !== 'undefined' ? log.personalFouls : 0) +
                            (typeof log.technicalFouls !== 'undefined' ? log.technicalFouls : 0) +
                            (typeof log.unsportsmanlikeFouls !== 'undefined' ? log.unsportsmanlikeFouls : 0) +
                            (log.disruptiveFouls || 0) +
                            (log.flagrantFouls || 0)
                    } else {
                        return acc
                    }
                }, 0)
                this.team2Fouls = this.feed.reduce((acc, log) => {
                    if ((this.quarter <= 4 ? (this.quarter - 1) * 10 : 3 * 10) <= parseInt(log.timestamp.split(':')[0]) && parseInt(log.timestamp.split(':')[0]) < this.quarter * 10 && this.team2 === log.teamId) {
                        return acc +
                            (typeof log.personalFouls !== 'undefined' ? log.personalFouls : 0) +
                            (typeof log.technicalFouls !== 'undefined' ? log.technicalFouls : 0) +
                            (typeof log.unsportsmanlikeFouls !== 'undefined' ? log.unsportsmanlikeFouls : 0) +
                            (log.disruptiveFouls || 0) +
                            (log.flagrantFouls || 0)
                    } else {
                        return acc
                    }
                }, 0)
            },
            getSecondsFromFeed() {
                return parseInt(this.feed[0].timestamp.split(':')[1])
            },
            getMinutesFromFeed() {
                return parseInt(this.feed[0].timestamp.split(':')[0])
            },
            getSafeTimer() {
                return Number.isFinite(this.timer) ? Math.max(0, this.timer) : 0
            },
            getSafeQuarter() {
                return Math.max(1, parseInt(this.quarter, 10) || 1)
            },
            getElapsedGameSeconds() {
                const quarter = this.getSafeQuarter()
                const quarterDurationSeconds = quarter <= 4 ? 10 * 60 : 5 * 60
                const remainingSeconds = Math.floor(this.getSafeTimer() / 1000)
                const boundedRemainingSeconds = Math.min(quarterDurationSeconds, Math.max(0, remainingSeconds))
                const elapsedInQuarter = quarterDurationSeconds - boundedRemainingSeconds
                const elapsedBeforeQuarter = quarter <= 4 ? (quarter - 1) * 10 * 60 : 4 * 10 * 60 + (quarter - 5) * 5 * 60

                return elapsedBeforeQuarter + elapsedInQuarter
            },
            getMinutes() {
                return Math.floor(this.getElapsedGameSeconds() / 60)
            },
            getSeconds() {
                const seconds = this.getElapsedGameSeconds() % 60
                return (seconds < 10 ? '0' : '') + seconds
            },
            getCurrentTimestamp() {
                return `${this.getMinutes()}:${this.getSeconds()}`
            },
            // follow the device: landscape layout when the device is turned sideways and the screen is wide enough for it
            // follow how the device is held (landscape layout needs a wide enough screen);
            // the view button can override that until the device is rotated again
            updateOrientation() {
                const deviceLandscape = this.isDeviceLandscape()
                if (this.lastDeviceLandscape !== null && deviceLandscape !== this.lastDeviceLandscape) {
                    this.orientationOverride = null
                }
                this.lastDeviceLandscape = deviceLandscape
                if (this.orientationOverride) {
                    this.orientation = this.orientationOverride
                } else {
                    this.orientation = deviceLandscape && window.innerWidth >= 600 ? 'landscape' : 'portrait'
                }
            },
            setOrientationOverride(value) {
                this.orientationOverride = value
                this.updateOrientation()
            },
            runLandscapeAction(category, side) {
                this.currentTeam = side
                if (category === 'sub') {
                    this.playersDialog = true
                } else if (category === 'foul') {
                    this.dialog = true
                    this.foulsDialog = true
                    this.category = 'foul'
                } else {
                    this.dialog = true
                    this.category = category
                }
            },
            // prefer the device orientation over the viewport: the on-screen keyboard shrinks the viewport
            // height and would otherwise flip a tablet to landscape while typing
            isDeviceLandscape() {
                if (window.screen && window.screen.orientation && window.screen.orientation.type) {
                    return window.screen.orientation.type.startsWith('landscape')
                }
                if (typeof window.orientation === 'number') {
                    return Math.abs(window.orientation) === 90
                }
                return window.innerWidth > window.innerHeight
            },
            getMatchValues(matchStats) {
                this.stats = [
                    { text: 'Pts', value: 'points', team1: { player: null, points: 0 }, team2: { player: null, points: 0 } },
                    { text: '2P%', value: 'two_points_percentage', team1: { player: null, twoPointAttempts: 0, twoPointsMade: 0, two_points_percentage: 0 }, team2: { player: null, twoPointAttempts: 0, twoPointsMade: 0, two_points_percentage: 0 } },
                    { text: '3P%', value: 'three_points_percentage', team1: { player: null, threePointAttempts: 0, threePointsMade: 0, three_points_percentage: 0 }, team2: { player: null, threePointAttempts: 0, threePointsMade: 0, three_points_percentage: 0 } },
                    { text: 'FT%', value: 'free_throw_percentage', team1: { player: null, freeThrowAttempts: 0, freeThrowsMade: 0, free_throw_percentage: 0 }, team2: { player: null, freeThrowAttempts: 0, freeThrowsMade: 0, free_throw_percentage: 0 } },
                    { text: 'OR', value: 'offensiveRebounds', team1: { player: null, offensiveRebounds: 0 }, team2: { player: null, offensiveRebounds: 0 } },
                    { text: 'DR', value: 'defensiveRebounds', team1: { player: null, defensiveRebounds: 0 }, team2: { player: null, defensiveRebounds: 0 } },
                    { text: 'TR', value: 'rebounds', team1: { player: null, rebounds: 0 }, team2: { player: null, rebounds: 0 } },
                    { text: 'AST', value: 'assists', team1: { player: null, assists: 0 }, team2: { player: null, assists: 0 } },
                    { text: 'STL', value: 'steals', team1: { player: null, steals: 0 }, team2: { player: null, steals: 0 } },
                    { text: 'TO', value: 'turnovers', team1: { player: null, turnovers: 0 }, team2: { player: null, turnovers: 0 } },
                    { text: 'BLK', value: 'blockedShots', team1: { player: null, blockedShots: 0 }, team2: { player: null, blockedShots: 0 } },
                    { text: 'FC', value: 'personalFouls', team1: { player: null, personalFouls: 0 }, team2: { player: null, personalFouls: 0 } },
                    { text: 'TFC', value: 'technicalFouls', team1: { player: null, technicalFouls: 0 }, team2: { player: null, technicalFouls: 0 } },
                    { text: 'USF', value: 'unsportsmanlikeFouls', team1: { player: null, unsportsmanlikeFouls: 0 }, team2: { player: null, unsportsmanlikeFouls: 0 } },
                    { text: 'DIF', value: 'disruptiveFouls', team1: { player: null, disruptiveFouls: 0 }, team2: { player: null, disruptiveFouls: 0 } },
                    { text: 'FLF', value: 'flagrantFouls', team1: { player: null, flagrantFouls: 0 }, team2: { player: null, flagrantFouls: 0 } }
                ]
                this.stats.forEach((stat) => {
                    matchStats.team1.players.forEach((player) => {
                        if (player.stats.length > 0) {
                            if (stat.value === 'two_points_percentage') {
                                stat.team1.twoPointAttempts += player.stats[0].twoPointAttempts
                                stat.team1.twoPointsMade += player.stats[0].twoPointsMade
                            } else if (stat.value === 'three_points_percentage') {
                                stat.team1.threePointAttempts += player.stats[0].threePointAttempts
                                stat.team1.threePointsMade += player.stats[0].threePointsMade
                            } else if (stat.value === 'free_throw_percentage') {
                                stat.team1.freeThrowAttempts += player.stats[0].freeThrowAttempts
                                stat.team1.freeThrowsMade += player.stats[0].freeThrowsMade
                            } else {
                                stat.team1[stat.value] += player.stats[0][stat.value]
                            }
                        }
                    })
                    stat.team1.two_points_percentage = (stat.team1.twoPointsMade / (stat.team1.twoPointAttempts ? stat.team1.twoPointAttempts : 1) * 100).toFixed(0)
                    stat.team1.three_points_percentage = (stat.team1.threePointsMade / (stat.team1.threePointAttempts ? stat.team1.threePointAttempts : 1) * 100).toFixed(0)
                    stat.team1.free_throw_percentage = (stat.team1.freeThrowsMade / (stat.team1.freeThrowAttempts ? stat.team1.freeThrowAttempts : 1) * 100).toFixed(0)
                    matchStats.team2.players.forEach((player) => {
                        if (player.stats.length > 0) {
                            if (stat.value === 'two_points_percentage') {
                                stat.team2.twoPointAttempts += player.stats[0].twoPointAttempts
                                stat.team2.twoPointsMade += player.stats[0].twoPointsMade
                            } else if (stat.value === 'three_points_percentage') {
                                stat.team2.threePointAttempts += player.stats[0].threePointAttempts
                                stat.team2.threePointsMade += player.stats[0].threePointsMade
                            } else if (stat.value === 'free_throw_percentage') {
                                stat.team2.freeThrowAttempts += player.stats[0].freeThrowAttempts
                                stat.team2.freeThrowsMade += player.stats[0].freeThrowsMade
                            } else {
                                stat.team2[stat.value] += player.stats[0][stat.value]
                            }
                        }
                    })
                    stat.team2.two_points_percentage = (stat.team2.twoPointsMade / (stat.team2.twoPointAttempts ? stat.team2.twoPointAttempts : 1) * 100).toFixed(0)
                    stat.team2.three_points_percentage = (stat.team2.threePointsMade / (stat.team2.threePointAttempts ? stat.team2.threePointAttempts : 1) * 100).toFixed(0)
                    stat.team2.free_throw_percentage = (stat.team2.freeThrowsMade / (stat.team2.freeThrowAttempts ? stat.team2.freeThrowAttempts : 1) * 100).toFixed(0)
                })
            },
            getMatchStats() {
                matchService.getMatchStats(this.$route.params.matchId).then(resp => {
                    this.matchStats = resp.data
                    this.getMatchValues(resp.data)
                    this.setMvp()
                })
            },
            setMvp() {
                if (this.matchStats.team1Score === 0 && this.matchStats.team2Score === 0) {
                    this.mvp = { id: null, points: 0 }
                } else if (this.matchStats.team1Score > this.matchStats.team2Score) {
                    const team1PlayersWithPir = this.matchStats.team1.players.filter(p => p.stats && p.stats[0] && typeof p.stats[0].pir !== 'undefined')
                    this.mvp = team1PlayersWithPir.length > 0 ? team1PlayersWithPir.reduce((max, p) => p.stats[0].pir > max.stats[0].pir ? p : max, team1PlayersWithPir[0]) : null
                } else {
                    const team2PlayersWithPir = this.matchStats.team2.players.filter(p => p.stats && p.stats[0] && typeof p.stats[0].pir !== 'undefined')
                    this.mvp = team2PlayersWithPir.length > 0 ? team2PlayersWithPir.reduce((max, p) => p.stats[0].pir > max.stats[0].pir ? p : max, team2PlayersWithPir[0]) : null
                }
            },
            getCategoryText(category) {
                switch (category) {
                case '1pt':
                    return 'POINTS: 1'
                case '2pts':
                    return 'POINTS: 2'
                case '3pts':
                    return 'POINTS: 3'
                case 'dreb':
                    return 'DEFENSIVE REBOUND'
                case 'oreb':
                    return 'OFFENSIVE REBOUND'
                case 'to':
                    return 'TURNOVER'
                case 'stl':
                    return 'STEALS'
                case 'asst':
                    return 'ASSISTS'
                case 'blk':
                    return 'BLOCKS'
                case 'foul':
                    if (!this.choice) {
                        return 'FOUL'
                    } else if (this.choice === 'personal') {
                        return 'PERSONAL FOUL'
                    } else if (this.choice === 'technical') {
                        return 'TECHNICAL FOUL (T1)'
                    } else if (this.choice === 'technical2') {
                        return 'TECHNICAL FOUL (T2)'
                    } else if (this.choice === 'disruptive') {
                        return 'DISRUPTIVE FOUL'
                    } else if (this.choice === 'flagrant') {
                        return 'FLAGRANT FOUL'
                    } else if (this.choice === 'coachTechnical') {
                        return 'COACH TECHNICAL FOUL'
                    } else if (this.choice === 'benchTechnical') {
                        return 'BENCH TECHNICAL FOUL'
                    } else {
                        return 'UNSPORTSMANLIKE FOUL'
                    }
                case 'sub-in':
                    return 'SUBSTITUTE IN ->'
                case 'sub-out':
                    return 'SUBSTITUTE OUT <-'
                default:
                    break
                }
            },
            sortPlayers(players) {
                if (players.length > 0) {
                    players.sort((a, b) => {
                        if ((a.alternativeJersey || a.alternativeJersey === 0 ? parseInt(a.alternativeJersey) : a.jersey) < (b.alternativeJersey || b.alternativeJersey === 0 ? parseInt(b.alternativeJersey) : b.jersey)) {
                            return -1
                        }
                        if ((a.alternativeJersey || a.alternativeJersey === 0 ? parseInt(a.alternativeJersey) : a.jersey) > (b.alternativeJersey || b.alternativeJersey === 0 ? parseInt(b.alternativeJersey) : b.jersey)) {
                            return 1
                        }
                        return 0
                    })
                } else {
                    return []
                }
            },
            playerLabel(player) {
                return player ? `#${this.jerseyOf(player)} ${player.surname || player.name}` : 'Team'
            },
            describeStat(player) {
                const shot = { '1pt': '1PT', '2pts': '2PT', '3pts': '3PT' }[this.category]
                const labels = {
                    dreb: 'Def. rebound',
                    oreb: 'Off. rebound',
                    to: 'Turnover',
                    stl: 'Steal',
                    asst: 'Assist',
                    blk: 'Block',
                    foul: {
                        technical: 'Technical foul (T1)',
                        technical2: 'Technical foul (T2)',
                        coachTechnical: 'Coach technical foul',
                        benchTechnical: 'Bench technical foul'
                    }[this.choice] ||
                        `${this.choice ? this.choice.charAt(0).toUpperCase() + this.choice.slice(1) : ''} foul`.trim(),
                    'sub-in': 'In',
                    'sub-out': 'Out'
                }
                const what = shot ? `${shot} ${this.choice === 'made' ? 'made' : 'missed'}` : (labels[this.category] || '')
                return `${this.playerLabel(player)} · ${what}`
            },
            // start of a stat request; batch keeps the previous snapshot so a swap (out + in) undoes as one
            startSaving(text, batch = false) {
                this.saving = true
                if (!batch || !this.feedSnapshot) {
                    this.feedSnapshot = new Set((this.feed || []).map(log => log.logId))
                    this.statToastLogIds = []
                }
                this.statToastText = batch && this.statToastText ? `${this.statToastText}  ·  ${text}` : text
                this.statToastNeutral = false
                this.statToast = false
            },
            finishSaving() {
                this.saving = false
                this.statToast = true
                // select-first mode: once a stat is fully done (no follow-up step open), start from a clean selection
                if (this.isSelectMode) {
                    this.$nextTick(() => {
                        if (!this.dialog && !this.shootingChartDialog && !this.playersDialog) {
                            this.clearSelection()
                        }
                    })
                }
            },
            trackNewLogs() {
                if (!this.feedSnapshot) return
                this.statToastLogIds = (this.feed || []).filter(log => !this.feedSnapshot.has(log.logId)).map(log => log.logId)
            },
            undoLastStat() {
                const ids = this.statToastLogIds.slice()
                if (!ids.length) {
                    this.statToast = false
                    return
                }
                const subs = (this.feed || []).some(log => ids.includes(log.logId) && (log.playerMatchStatus === 'stop' || log.playerMatchStatus === 'begin'))
                this.undoing = true
                // the follow-up step (assist, rebound, turnover) belongs to the undone stat
                this.closeStatDialog()
                ids.reduce((chain, id) => chain.then(() => matchService.deleteMatchLog(this.match.id, id)), Promise.resolve())
                    .then(() => {
                        this.statToastText = 'Undone'
                        this.statToastNeutral = true
                        this.statToastLogIds = []
                        this.feedSnapshot = null
                        if (subs) {
                            this.getActivePlayers()
                        }
                        return this.getMatch()
                    })
                    .catch(() => {
                        this.snackbarText = 'Could not undo, delete it from the feed'
                        this.snackbarColor = 'error'
                        this.showSnackbar = true
                    })
                    .finally(() => {
                        this.undoing = false
                        this.statToast = this.statToastNeutral
                    })
            },
            // with advanced stats on, the shot details come first (then the shot goes on as below)
            onShotPlaced(shots) {
                this.shots = shots
                if (!shots.length) return
                this.shootingChartDialog = false
                if (this.advancedStats && (this.category === '2pts' || this.category === '3pts')) {
                    this.shotDetailsShots = shots.slice()
                    this.shotDetailsStart = this.suggestShotDetails()
                    this.shotDetailsEdit = null
                    this.shotDetailsDialog = true
                    return
                }
                this.shotDetails = null
                this.continueShot(shots)
            },
            // the tap on the court is the save for a made shot; a missed one waits for the rebound screen
            continueShot(shots) {
                this.shots = shots
                if (this.choice === 'made') {
                    this.setStat(this.selectedPlayer)
                    return
                }
                this.pendingMiss = {
                    category: this.category,
                    player: this.selectedPlayer,
                    team: this.currentTeam,
                    shots: shots.slice(),
                    shotDetails: this.shotDetails
                }
                this.shotDetails = null
                this.pointsDialog = false
                this.statDialog = false
                this.dialog = true
                this.reboundDialog = true
            },
            // puts back the state of the pending miss so setStat / putBack build the same request as before
            restorePendingMiss() {
                const miss = this.pendingMiss
                this.pendingMiss = null
                this.currentTeam = miss.team
                this.category = miss.category
                this.choice = 'missed'
                this.shots = miss.shots
                this.shotDetails = miss.shotDetails || null
                this.selectedPlayer = miss.player
                return miss
            },
            // saves the pending miss without opening the rebound screen again
            savePendingMiss() {
                const miss = this.restorePendingMiss()
                return Promise.resolve(this.setStat(miss.player, { followUp: false }))
            },
            noRebound() {
                if (!this.pendingMiss) {
                    this.closeStatDialog()
                    return
                }
                this.reboundDialog = false
                this.dialog = false
                this.savePendingMiss()
            },
            // shooter took his own rebound and scored: one put-back record, no separate miss
            putBackPending() {
                const miss = this.restorePendingMiss()
                this.reboundDialog = false
                this.dialog = false
                this.putBack(miss.player)
            },
            // one-screen rebound after a missed shot: player (or team) of either side, or skip
            pickRebound(type, player) {
                if (player && !this.isAvailable(player)) {
                    this.openUnavailableDialog(player)
                    return
                }
                if (this.pendingMiss) {
                    const shooterTeam = this.pendingMiss.team
                    this.reboundDialog = false
                    this.dialog = false
                    this.savePendingMiss().then(saved => {
                        if (!saved) return
                        this.currentTeam = shooterTeam
                        this.batchNextStat = true
                        this.saveRebound(type, player)
                        this.batchNextStat = false
                    })
                    return
                }
                this.saveRebound(type, player)
            },
            saveRebound(type, player) {
                if (type === 'dreb') {
                    this.currentTeam = this.currentTeam === 1 ? 2 : 1
                }
                this.category = type
                this.reboundDialog = false
                this.selectedPlayer = player || null
                this.dialog = false
                this.statDialog = false
                this.setStat(this.selectedPlayer)
            },
            // player from the pick step; the type of a foul was picked before it, `made` is only about shots
            pickPlayer(player) {
                if (!this.isAvailable(player)) {
                    this.openUnavailableDialog(player)
                    return
                }
                this.selectedPlayer = player
                this.setChoice(this.category === 'foul' ? this.choice : this.made)
            },
            // team stat from the pick step (team rebound / team turnover)
            pickTeam() {
                this.dialog = false
                this.statDialog = false
                this.selectedPlayer = null
                this.setStat(null)
            },
            // a technical foul of the coach or of the bench: the team's, with no player
            pickTeamFoul(kind) {
                this.category = 'foul'
                this.choice = kind
                this.pickTeam()
            },
            // substitutions: tap a player on court, then the bench player who replaces him
            toggleSwapOut(player) {
                this.swapOutPlayer = this.swapOutPlayer && this.swapOutPlayer.id === player.id ? null : player
            },
            subIn(player) {
                if (!this.swapOutPlayer) {
                    this.addPlayer(player)
                    return
                }
                const out = this.swapOutPlayer
                this.swapOutPlayer = null
                this.removePlayer(out)
                this.batchNextStat = true
                this.addPlayer(player)
                this.batchNextStat = false
            },
            setChoice(choice) {
                this.choice = choice
                if (this.selectedPlayer) {
                    if (this.category === '2pts' || this.category === '3pts') {
                        this.shootingChartDialog = true
                    } else {
                        this.setStat(this.selectedPlayer)
                    }
                }
            },
            endMatch() {
                let data = {
                    content: this.getCurrentTimestamp()
                }
                matchService.endMatch(this.$route.params.matchId, data).then(resp => {
                    this.endMatchDialog = false
                    this.$router.go(-1)
                })
            },
            addPlayer(player) {
                if (!this.isAvailable(player)) {
                    this.openUnavailableDialog(player)
                    return
                }
                if (this.currentTeam === 1 ? this.team1SelectedPlayers.length < 5 : this.team2SelectedPlayers.length < 5) {
                    this.selectedPlayer = player
                    if (this.currentTeam === 1) {
                        this.team1SelectedPlayers = [...this.team1SelectedPlayers, player]
                        this.sortPlayers(this.team1SelectedPlayers)
                        this.sortPlayers(this.team1BenchPlayers)
                    } else {
                        this.team2SelectedPlayers = [...this.team2SelectedPlayers, player]
                        this.sortPlayers(this.team2SelectedPlayers)
                        this.sortPlayers(this.team2BenchPlayers)
                    }
                    this.category = 'sub-in'
                    this.setStat(player)
                }
            },
            removePlayer(player) {
                this.selectedPlayer = player
                if (this.currentTeam === 1 ? this.team1SelectedPlayers.length > 0 : this.team2SelectedPlayers.length > 0) {
                    if (this.currentTeam === 1) {
                        this.team1SelectedPlayers = this.team1SelectedPlayers.filter(element => player.id !== element.id)
                        this.sortPlayers(this.team1SelectedPlayers)
                        this.sortPlayers(this.team1BenchPlayers)
                    } else {
                        this.team2SelectedPlayers = this.team2SelectedPlayers.filter(element => player.id !== element.id)
                        this.sortPlayers(this.team2SelectedPlayers)
                        this.sortPlayers(this.team2BenchPlayers)
                    }
                    this.category = 'sub-out'
                    this.setStat(player)
                }
            },
            getTeamLogo(log) {
                return this.googleApiHost + '/' + (this.match.team1.id === log.teamId ? this.match.team1.logo : this.match.team2.logo)
            },
            getMatch() {
                return matchService.getMatch(this.$route.params.matchId).then(resp => {
                    resp.data.gameRosterTeam1 = resp.data.gameRosterTeam1 || []
                    resp.data.gameRosterTeam2 = resp.data.gameRosterTeam2 || []
                    this.match = resp.data
                    if (this.match.matchDatetime === null) {
                        this.matchDate = null
                        this.matchTime = null
                    } else {
                        this.matchDate = momentTimezone(this.match.matchDatetime).tz('Europe/Athens').locale('el-GR').format('dddd D/M/y, HH:mm').toString()
                    }
                    this.team1 = resp.data.team1.id
                    this.team2 = resp.data.team2.id
                    // return promises for fetching team players, then populate active players
                    return Promise.all([
                        this.fetchFilteredTeam1Players(resp.data.team1.id),
                        this.fetchFilteredTeam2Players(resp.data.team2.id)
                    ]).then(() => {
                        this.sortPlayers(this.team1Players)
                        this.sortPlayers(this.team2Players)
                        this.getActivePlayers()
                    })
                })
            },
            getActivePlayers() {
                let newTeam1Selected = []
                let newTeam2Selected = []
                matchService.getActivePlayers(this.$route.params.matchId).then(resp => {
                    resp.data.team1Players.forEach(element => {
                        const found = this.team1Players.find(player => player.id === element.playerId)
                        if (found) newTeam1Selected.push(found)
                    })
                    resp.data.team2Players.forEach(element => {
                        const found = this.team2Players.find(player => player.id === element.playerId)
                        if (found) newTeam2Selected.push(found)
                    })
                    this.team1SelectedPlayers = [...newTeam1Selected]
                    this.team2SelectedPlayers = [...newTeam2Selected]
                    this.sortPlayers(this.team1SelectedPlayers)
                    this.sortPlayers(this.team2SelectedPlayers)
                })
            },
            getLogText(log) {
                let temp = ''
                Object.keys(log).forEach(key => {
                    if (key === 'defensiveRebounds') {
                        temp += 'defensive rebound'
                    } else if (key === 'offensiveRebounds') {
                        temp += 'offensive rebound'
                    } else if (key === 'turnovers') {
                        temp += 'turnover'
                    } else if (key === 'turnovers') {
                        temp += 'turnover'
                    } else if (key === 'steals') {
                        temp += 'steal'
                    } else if (key === 'assists') {
                        temp += 'assist'
                    } else if (key === 'playerMatchStatus' && log[key] === 'begin') {
                        temp += 'subbed in'
                    } else if (key === 'playerMatchStatus' && log[key] === 'stop') {
                        temp += 'subbed out'
                    } else if (key === 'blockedShots') {
                        temp += 'block'
                    } else if (key === 'personalFouls') {
                        temp += 'personal foul'
                    } else if (key === 'technicalFouls') {
                        temp += log.category2TechnicalFouls ? 'technical foul (T2)' : 'technical foul'
                    } else if (key === 'unsportsmanlikeFouls') {
                        temp += 'unsportsmanlike foul'
                    } else if (key === 'disruptiveFouls' && log[key]) {
                        temp += 'disruptive foul'
                    } else if (key === 'flagrantFouls' && log[key]) {
                        temp += 'flagrant foul'
                    } else if (key === 'coachTechnicalFouls' && log[key]) {
                        temp += 'coach technical foul'
                    } else if (key === 'benchTechnicalFouls' && log[key]) {
                        temp += 'bench technical foul'
                    } else if (key === 'freeThrowsMade' && log[key] === 1) {
                        temp += 'free throw made'
                    } else if (key === 'freeThrowAttempts' && log['freeThrowsMade'] === 0) {
                        temp += 'free throw missed'
                    } else if (key === 'twoPointsMade' && log[key] === 1) {
                        temp += '2pt made'
                    } else if (key === 'twoPointAttempts' && log['twoPointsMade'] === 0) {
                        temp += '2pt missed'
                    } else if (key === 'threePointsMade' && log[key] === 1) {
                        temp += '3pt made'
                    } else if (key === 'threePointAttempts' && log['threePointsMade'] === 0) {
                        temp += '3pt missed'
                    }
                })
                if (log.playerId === 0) {
                    return `team ${temp}`
                } else {
                    return `#${log.alternativeJersey || log.alternativeJersey === 0 ? log.alternativeJersey : log.jersey} ${temp}`
                }
            },
            getFeed() {
                return matchService.getMatchLogs(this.$route.params.matchId).then((resp) => {
                    if (resp.data.length === 0) {
                        this.feed = []
                        return
                    }
                    this.feed = resp.data
                        .sort((a, b) => {
                            const toMs = s => {
                                const parts = (s || '').split(':')
                                const min = parseInt(parts[0], 10) || 0
                                const sec = parseInt(parts[1], 10) || 0
                                return min * 60000 + sec * 1000
                            }
                            // descending: newest (largest elapsed) first
                            return toMs(b.timestamp) - toMs(a.timestamp)
                        })
                    this.setQuarterFouls()
                    this.trackNewLogs()
                })
            },
            // Clock and period on opening: from the server (the clock's last event, as it is now), or, when it
            // has none (no clock event yet, or a server restart), from the time of the last stat, stopped
            restoreClock() {
                return matchService.getTimerState(this.match.id)
                    .then(resp => resp.data || null)
                    .catch(() => null)
                    .then(state => {
                        if (state && state.period) {
                            this.quarter = Number(state.period) || 1
                            this.setClockValue(Number(state.timer) || 0)
                            if (state.timerEventStatus === 'start' && this.timer > 0) {
                                this.startClock()
                            }
                            return
                        }
                        // the server has no clock (a restart): the one of the play-by-play, sent to the live stream too
                        return this.getFeed().then(() => {
                            this.restoreClockFromFeed()
                            return this.feed && this.feed.length > 0
                        })
                    })
                    // the clock is usable even when the play-by-play could not be read
                    .catch(() => false)
                    .then(fromFeed => {
                        // after the period watcher has run for the restored period, so it does not reset the clock
                        this.$nextTick(() => {
                            this.clockReady = true
                            if (fromFeed) this.sendClockEvent('stop')
                        })
                    })
            },
            restoreClockFromFeed() {
                const last = this.feed && this.feed[0]
                if (!last || !last.timestamp) {
                    this.quarter = 1
                    this.setClockValue(this.getPeriodDurationMs(1))
                    return
                }
                const [minStr = '0', secStr = '0'] = last.timestamp.split(':')
                const elapsedSeconds = (parseInt(minStr, 10) || 0) * 60 + (parseInt(secStr, 10) || 0)
                // 4 periods of 10 minutes, then overtimes of 5
                const quarter = elapsedSeconds < 40 * 60
                    ? Math.floor(elapsedSeconds / 600) + 1
                    : Math.floor((elapsedSeconds - 40 * 60) / 300) + 5
                const quarterStart = quarter <= 4 ? (quarter - 1) * 600 : 40 * 60 + (quarter - 5) * 300
                const remaining = this.getPeriodDurationMs(quarter) / 1000 - (elapsedSeconds - quarterStart)
                this.quarter = quarter
                this.setClockValue(Math.max(0, remaining) * 1000)
            },
            deleteLog() {
                let log = this.feed.filter((element) => this.logId === element.logId)[0]
                matchService.deleteMatchLog(this.match.id, this.logId).then((resp) => {
                    this.deleteDialog = false
                    this.feed = resp.data
                    if (log.playerMatchStatus === 'stop' || log.playerMatchStatus === 'begin') {
                        this.getActivePlayers()
                    }
                    this.getMatch()
                })
            },
            putBack(selectedPlayer) {
                this.retry = false
                this.startSaving(`${this.playerLabel(selectedPlayer)} · ${this.category === '3pts' ? '3PT' : '2PT'} put back`)
                let data = {
                    period: this.quarter,
                    teamId: this.currentTeam === 1 ? this.team1 : this.team2,
                    playerId: selectedPlayer.id,
                    jersey: selectedPlayer.jersey,
                    alternativeJersey: selectedPlayer.alternativeJersey,
                    timestamp: this.getCurrentTimestamp()
                }
                switch (this.category) {
                case '2pts':
                    data.throwCoordinates = { ...this.shots[0], throwStatus: 'putback' }
                    data.twoPointsMade = 1
                    data.twoPointAttempts = 2
                    data.offensiveRebounds = 1
                    break
                case '3pts':
                    data.throwCoordinates = { ...this.shots[0], throwStatus: 'putback' }
                    data.threePointsMade = 1
                    data.threePointAttempts = 2
                    break
                default:
                    break
                }
                Object.assign(data, this.takeShotDetails())
                matchService.importMatchLog(this.match.id, data).then((resp) => {
                    this.choice = null
                    this.category = null
                    this.pointsDialog = false
                    this.foulsDialog = false
                    this.reboundDialog = false
                    this.dialog = false
                    this.finishSaving()
                    resp.data.gameRosterTeam1 = resp.data.gameRosterTeam1 || []
                    resp.data.gameRosterTeam2 = resp.data.gameRosterTeam2 || []
                    this.match = resp.data
                    this.selectedPlayer = null
                }).catch(() => {
                    this.dialog = false
                    this.pointsDialog = false
                    this.foulsDialog = false
                    this.reboundDialog = false
                    this.playersDialog = false
                    this.saving = false
                    this.statToast = false
                    this.retry = true
                })
            },
            // options.followUp = false: don't open the rebound screen after a missed shot
            setStat(player, options = {}) {
                if (!this.isAvailable(player) && this.category !== 'sub-out') {
                    this.openUnavailableDialog(player)
                    return
                }
                this.retry = false
                this.startSaving(this.describeStat(player), this.batchNextStat)
                // remembered for the assist step: the shooter can't assist himself
                this.lastStatPlayerId = player ? player.id : null
                let data = {
                    period: this.quarter,
                    teamId: this.currentTeam === 1 ? this.team1 : this.team2,
                    playerId: player ? player.id : 0,
                    jersey: player ? player.jersey : null,
                    alternativeJersey: player ? player.alternativeJersey : null,
                    timestamp: this.getCurrentTimestamp()
                }
                switch (this.category) {
                case '1pt':
                    if (this.choice === 'made') {
                        data.freeThrowsMade = 1
                        data.freeThrowAttempts = 1
                    } else {
                        data.freeThrowAttempts = 1
                    }
                    break
                case '2pts':
                    data.throwCoordinates = this.shots[0]
                    if (this.choice === 'made') {
                        data.twoPointsMade = 1
                        data.twoPointAttempts = 1
                    } else {
                        data.twoPointAttempts = 1
                    }
                    Object.assign(data, this.takeShotDetails())
                    break
                case '3pts':
                    console.log('in 3pts')
                    console.log(this.shots)
                    data.throwCoordinates = this.shots[0]
                    if (this.choice === 'made') {
                        data.threePointsMade = 1
                        data.threePointAttempts = 1
                    } else {
                        data.threePointAttempts = 1
                    }
                    Object.assign(data, this.takeShotDetails())
                    break
                case 'dreb':
                    data.defensiveRebounds = 1
                    break
                case 'oreb':
                    data.offensiveRebounds = 1
                    break
                case 'to':
                    data.turnovers = 1
                    break
                case 'stl':
                    data.steals = 1
                    break
                case 'asst':
                    data.assists = 1
                    break
                case 'blk':
                    data.blockedShots = 1
                    break
                case 'foul':
                    if (this.choice === 'personal') {
                        data.personalFouls = 1
                    } else if (this.choice === 'technical') {
                        data.technicalFouls = 1
                    } else if (this.choice === 'technical2') {
                        // a technical foul like any other, marked as of category 2
                        data.technicalFouls = 1
                        data.category2TechnicalFouls = 1
                    } else if (this.choice === 'disruptive') {
                        data.disruptiveFouls = 1
                    } else if (this.choice === 'flagrant') {
                        data.flagrantFouls = 1
                    } else if (this.choice === 'coachTechnical') {
                        data.coachTechnicalFouls = 1
                    } else if (this.choice === 'benchTechnical') {
                        data.benchTechnicalFouls = 1
                    }
                    break
                case 'sub-in':
                    data.playerMatchStatus = 'begin'
                    break
                case 'sub-out':
                    data.playerMatchStatus = 'stop'
                    break
                default:
                    break
                }
                return matchService.importMatchLog(this.match.id, data).then((resp) => {
                    if ((this.category === '2pts' || this.category === '3pts') && this.choice === 'made') {
                        this.choice = null
                        this.pointsDialog = false
                        this.foulsDialog = false
                        this.reboundDialog = false
                        this.dialog = false
                        this.finishSaving()
                        this.dialog = true
                        this.category = 'asst'
                    } else if ((this.category === '2pts' || this.category === '3pts' || this.category === '1pt') && this.choice === 'missed') {
                        this.choice = null
                        this.pointsDialog = false
                        this.foulsDialog = false
                        this.dialog = false
                        this.finishSaving()
                        // every missed shot (free throws too) goes to the rebound screen; it has a Skip.
                        // A shot saved from the rebound screen already knows what comes next.
                        if (options.followUp !== false) {
                            this.reboundDialog = true
                            this.dialog = true
                        }
                    } else if (this.category === 'stl') {
                        this.currentTeam = this.currentTeam === 1 ? 2 : 1
                        this.choice = null
                        this.pointsDialog = false
                        this.foulsDialog = false
                        this.reboundDialog = false
                        this.dialog = false
                        this.finishSaving()
                        this.dialog = true
                        this.category = 'to'
                    } else if (this.category === 'foul') {
                        this.finishSaving()
                        this.dialog = false
                        this.pointsDialog = false
                        this.foulsDialog = false
                        this.category = null
                        this.choice = null
                        this.reboundDialog = false
                        this.pauseTimer()
                    } else {
                        this.dialog = false
                        this.pointsDialog = false
                        this.foulsDialog = false
                        this.category = null
                        this.choice = null
                        this.reboundDialog = false
                        this.finishSaving()
                    }
                    resp.data.gameRosterTeam1 = resp.data.gameRosterTeam1 || []
                    resp.data.gameRosterTeam2 = resp.data.gameRosterTeam2 || []
                    this.match = resp.data
                    this.selectedPlayer = null
                    return true
                }).catch(() => {
                    this.dialog = false
                    this.pointsDialog = false
                    this.foulsDialog = false
                    this.reboundDialog = false
                    this.playersDialog = false
                    this.saving = false
                    this.statToast = false
                    this.retry = true
                    return false
                })
            },
            fetchFilteredTeam1Players(id) {
                this.loading = true
                return teamService.getTeam(id).then(resp => {
                    this.team1Players = resp.data.players.filter(element => element.active === true)
                    this.loading = false
                })
            },
            fetchFilteredTeam2Players(id) {
                this.loading = true
                return teamService.getTeam(id).then(resp => {
                    this.team2Players = resp.data.players.filter(element => element.active === true)
                    this.loading = false
                })
            },
            getPlayerText(item) {
                return `#${item.jersey} ${item.name}`
            },
            // openFullscreen() {
            //     var elem = document.getElementById('stats')
            //     if (elem.requestFullscreen) {
            //         elem.requestFullscreen()
            //     } else if (elem.webkitRequestFullscreen) { /* Safari */
            //         elem.webkitRequestFullscreen()
            //     } else if (elem.msRequestFullscreen) { /* IE11 */
            //         elem.msRequestFullscreen()
            //     }
            // },
            getPeriodDurationMs(quarter) {
                return (quarter <= 4 ? 10 : 5) * 60000
            },
            // Shows `ms` on the clock; a running clock goes on from it
            setClockValue(ms) {
                this.timer = Math.max(0, Number(ms) || 0)
                if (this.running) {
                    this.clockStartedAt = Date.now()
                    this.clockStartValue = this.timer
                }
            },
            startClock() {
                if (this.timer <= 0) return
                this.running = true
                this.clockStartedAt = Date.now()
                this.clockStartValue = this.timer
                clearInterval(this.clockInterval)
                this.clockInterval = setInterval(this.tickClock, 250)
            },
            stopClock() {
                if (this.running) this.tickClock()
                this.running = false
                clearInterval(this.clockInterval)
                this.clockInterval = null
            },
            tickClock() {
                if (!this.running) return
                const remaining = this.clockStartValue - (Date.now() - this.clockStartedAt)
                // whole seconds: 09:59 right after the start, 00:00 when the time is up
                this.timer = Math.max(0, Math.ceil(remaining / 1000) * 1000)
                if (remaining <= 0) this.onPeriodTimeUp()
            },
            // The time of the period is up: the live stream shows its break screen, the next period is set up (stopped)
            onPeriodTimeUp() {
                this.running = false
                clearInterval(this.clockInterval)
                this.clockInterval = null
                this.sendClockEvent('period_end', { timer: 0, period: this.quarter })
                // the period watcher sets the next period's time and sends the stopped clock
                this.quarter = this.quarter + 1
            },
            // Sends a clock event to the live stream. A failed one is tried again (1s, 2s, 4s) unless a newer
            // event has gone out since; `fixed` keeps its values, otherwise the clock as it is when sent
            sendClockEvent(status, fixed = null) {
                if (!this.clockReady || !this.match.id) return
                const seq = ++this.clockEventSeq
                this.clockSync = 'pending'
                const attempt = (tries) => {
                    const timer = fixed ? fixed.timer : this.timer
                    const period = fixed ? fixed.period : this.quarter
                    matchService.editTimer(this.match.id, status, timer, period).then(() => {
                        if (seq === this.clockEventSeq) this.clockSync = 'ok'
                    }).catch(() => {
                        // period_end only switches the screen, it is still worth sending after a newer event
                        if (seq !== this.clockEventSeq && status !== 'period_end') return
                        if (tries < 3) {
                            setTimeout(() => attempt(tries + 1), 1000 * 2 ** tries)
                        } else if (seq === this.clockEventSeq) {
                            this.clockSync = 'failed'
                        }
                    })
                }
                attempt(0)
            },
            // The clock as it is, again (the sync mark on the clock): after events that did not get through
            resyncClock() {
                this.sendClockEvent(this.running ? 'start' : 'stop')
            },
            switchTimer() {
                if (this.running) {
                    this.stopClock()
                    this.sendClockEvent('stop')
                } else if (this.timer > 0) {
                    this.startClock()
                    this.sendClockEvent('start')
                }
            },
            pauseTimer() {
                this.stopClock()
                this.sendClockEvent('stop')
            },
            timeout() {
                this.stopClock()
                this.sendClockEvent('timeout')
            },
            // The time set in the edit dialog: a running clock goes on from it, and the live stream gets it at once
            changeTimer() {
                const minutes = Math.max(0, parseInt(this.newMinutes, 10) || 0)
                const seconds = Math.min(59, Math.max(0, parseInt(this.newSeconds, 10) || 0))
                this.setClockValue(minutes * 60000 + seconds * 1000)
                this.newMinutes = minutes
                this.newSeconds = seconds
                this.sendClockEvent('edit')
            },
            // value: digits, or null to go back to the official jersey; done(ok) closes the editor
            updateSelectedPlayerAlternativeJersey(value, done) {
                const player = this.selectedPlayer
                const request = value === null
                    ? playerService.clearAlternativeJerseyOnPlayer(player.id)
                    : playerService.setAlternativeJerseyOnPlayer(player.id, value)
                request.then(() => {
                    // the number changes on screen only once it is saved
                    this.$set(player, 'alternativeJersey', value)
                    if (this.currentTeam === 1) {
                        this.sortPlayers(this.team1SelectedPlayers)
                        this.sortPlayers(this.team1BenchPlayers)
                        this.sortPlayers(this.team1Players)
                    } else {
                        this.sortPlayers(this.team2SelectedPlayers)
                        this.sortPlayers(this.team2BenchPlayers)
                        this.sortPlayers(this.team2Players)
                    }
                    this.snackbarText = value === null ? `Back to the official jersey #${player.jersey}` : `Jersey changed to #${value}`
                    this.snackbarColor = 'success'
                    this.showSnackbar = true
                    if (done) done(true)
                }).catch(() => {
                    this.snackbarText = 'Failed to update the jersey'
                    this.snackbarColor = 'error'
                    this.showSnackbar = true
                    if (done) done(false)
                })
            }
        }
    }
</script>

<style src="../components/stats/stats-live.css"></style>

<style>
/* ---------- match colours ---------- */
.match-colour + .match-colour {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.match-colour__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.match-colour__logo {
    flex: 0 0 28px;
}

.match-colour__name {
    flex: 1 1 auto;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.87);
}

.match-colour__sample {
    padding: 6px 14px;
    border-radius: 6px;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.match-colour__swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.match-colour__swatch {
    width: 34px;
    height: 34px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    border-radius: 50%;
    cursor: pointer;
}

.match-colour__swatch--on {
    box-shadow: 0 0 0 3px #ffffff, 0 0 0 5px rgba(0, 0, 0, 0.6);
}

.match-colour__custom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
}

.match-colour__picker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    cursor: pointer;
}

.match-colour__picker input {
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
}
</style>
